import React, { useEffect, useState, useRef, createElement } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { useNavigate, useLocation } from 'react-router-dom';
interface EmotionData {
  name: string;
  sector: string;
  theta: number;
  phi: number;
  color: {
    r: number;
    g: number;
    b: number;
  };
  stokesVector: {
    x: number;
    y: number;
    z: number;
  };
}
interface UnifiedQuantumVisualizationProps {
  emotion1: EmotionData;
  emotion2: EmotionData;
  updateEmotionParams?: (params: {
    theta1: number;
    phi1: number;
    coherence1: number;
    theta2: number;
    phi2: number;
    coherence2: number;
  }) => void;
}
// Visualization mode types
type VisualizationMode = 'baseline' | 'resonanceTransfer' | 'interferenceImpact' | 'entangledFusion';
export const UnifiedQuantumVisualization: React.FC<UnifiedQuantumVisualizationProps> = ({
  emotion1,
  emotion2,
  updateEmotionParams
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  // Global control states
  const [particleSpeed, setParticleSpeed] = useState(1.0);
  const [particleSize, setParticleSize] = useState(1.0);
  const [particleCount, setParticleCount] = useState(8000);
  // Glow control states
  const [particleGlow, setParticleGlow] = useState(1.0);
  const [glowRadius, setGlowRadius] = useState(1.0); // Control for glow size/radius
  const [glowColor, setGlowColor] = useState('#ffffff'); // Default glow color (white)
  // Trail control states
  const [trailEnabled, setTrailEnabled] = useState(false);
  const [trailLength, setTrailLength] = useState(0.5); // 0-1 scale for trail length
  const [trailBrightness, setTrailBrightness] = useState(0.5); // 0-1 scale for trail brightness
  // Movement amplification control
  const [movementAmplification, setMovementAmplification] = useState(1.0); // 0-10 scale for movement exaggeration
  const [needsReinitialize, setNeedsReinitialize] = useState(false);
  // Sphere control states
  const [sphereDistance, setSphereDistance] = useState(30); // Default distance between spheres (30 units)
  const [sphere1Size, setSphere1Size] = useState(1.0); // Scale factor for sphere 1
  const [sphere2Size, setSphere2Size] = useState(1.0); // Scale factor for sphere 2
  // Custom sphere colors for gradient effects
  const [sphere1Color, setSphere1Color] = useState('#0000ff'); // Default blue for sphere 1
  const [sphere2Color, setSphere2Color] = useState('#ff0000'); // Default red for sphere 2
  const [useCustomColors, setUseCustomColors] = useState(false); // Toggle for using custom colors
  const [colorGradientSteps, setColorGradientSteps] = useState(7); // Number of color steps in gradient
  const [colorIntensity, setColorIntensity] = useState(1.5); // Intensity multiplier for colors
  // Add background color state
  const [backgroundColor, setBackgroundColor] = useState('#111111');
  // Add camera position state variables
  const [cameraPosition, setCameraPosition] = useState({
    x: 0,
    y: 15,
    z: 40
  });
  const [cameraTarget, setCameraTarget] = useState({
    x: 0,
    y: 0,
    z: 0
  });
  // Previous state for animations
  const prevEmotion1Ref = useRef<EmotionData>(emotion1);
  const prevEmotion2Ref = useRef<EmotionData>(emotion2);
  const animationProgressRef = useRef(1.0); // 0 to 1 for transitions
  // Visualization mode state
  const [visualizationMode, setVisualizationMode] = useState<VisualizationMode>('baseline');
  // UI state for which controls panel is active
  const [activeControlPanel, setActiveControlPanel] = useState<'particles' | 'glow' | 'trails' | 'spheres' | 'colors'>('particles');
  // State for collapsible panels
  const [infoPanelVisible, setInfoPanelVisible] = useState(true);
  const [modePanelVisible, setModePanelVisible] = useState(true);
  const [controlPanelVisible, setControlPanelVisible] = useState(true);
  const [hintVisible, setHintVisible] = useState(true);
  // Alert state for share view
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  // Embed code modal state
  const [showEmbedModal, setShowEmbedModal] = useState(false);
  const [embedCode, setEmbedCode] = useState('');
  // Flag to prevent initial URL hash update
  const initialLoadCompleteRef = useRef(false);
  // Parse URL hash on component mount
  useEffect(() => {
    if (location.hash) {
      try {
        const hashParams = new URLSearchParams(location.hash.substring(1));
        // Parse visualization mode
        const mode = hashParams.get('mode');
        if (mode && ['baseline', 'resonanceTransfer', 'interferenceImpact', 'entangledFusion'].includes(mode)) {
          setVisualizationMode(mode as VisualizationMode);
        }
        // Parse particle settings
        const count = hashParams.get('count');
        if (count) setParticleCount(parseInt(count));
        const speed = hashParams.get('speed');
        if (speed) setParticleSpeed(parseFloat(speed));
        const size = hashParams.get('size');
        if (size) setParticleSize(parseFloat(size));
        // Parse glow settings
        const glow = hashParams.get('glow');
        if (glow) setParticleGlow(parseFloat(glow));
        const glowRad = hashParams.get('glowRadius');
        if (glowRad) setGlowRadius(parseFloat(glowRad));
        const glowCol = hashParams.get('glowColor');
        if (glowCol) setGlowColor(glowCol);
        // Parse movement amplification
        const moveAmp = hashParams.get('moveAmp');
        if (moveAmp) setMovementAmplification(parseFloat(moveAmp));
        // Parse sphere settings
        const dist = hashParams.get('sphereDist');
        if (dist) setSphereDistance(parseFloat(dist));
        const s1size = hashParams.get('sphere1Size');
        if (s1size) setSphere1Size(parseFloat(s1size));
        const s2size = hashParams.get('sphere2Size');
        if (s2size) setSphere2Size(parseFloat(s2size));
        // Parse trail settings
        const trails = hashParams.get('trails');
        if (trails) setTrailEnabled(trails === 'true');
        const trailLen = hashParams.get('trailLength');
        if (trailLen) setTrailLength(parseFloat(trailLen));
        const trailBright = hashParams.get('trailBright');
        if (trailBright) setTrailBrightness(parseFloat(trailBright));
        // Parse custom color settings
        const useCustom = hashParams.get('useCustomColors');
        if (useCustom) setUseCustomColors(useCustom === 'true');
        const s1color = hashParams.get('sphere1Color');
        if (s1color) setSphere1Color(s1color);
        const s2color = hashParams.get('sphere2Color');
        if (s2color) setSphere2Color(s2color);
        const gradientSteps = hashParams.get('colorGradientSteps');
        if (gradientSteps) setColorGradientSteps(parseInt(gradientSteps));
        const colorIntens = hashParams.get('colorIntensity');
        if (colorIntens) setColorIntensity(parseFloat(colorIntens));
        // Parse background color - THIS WAS MISSING
        const bgColor = hashParams.get('bgColor');
        if (bgColor) setBackgroundColor(bgColor);
        // Parse camera position parameters
        const camX = hashParams.get('camX');
        const camY = hashParams.get('camY');
        const camZ = hashParams.get('camZ');
        if (camX && camY && camZ) {
          setCameraPosition({
            x: parseFloat(camX),
            y: parseFloat(camY),
            z: parseFloat(camZ)
          });
        }
        // Parse camera target parameters
        const targetX = hashParams.get('targetX');
        const targetY = hashParams.get('targetY');
        const targetZ = hashParams.get('targetZ');
        if (targetX && targetY && targetZ) {
          setCameraTarget({
            x: parseFloat(targetX),
            y: parseFloat(targetY),
            z: parseFloat(targetZ)
          });
        }
      } catch (error) {
        console.error('Error parsing URL hash parameters:', error);
      }
    }
    // Mark initial load as complete so future state changes will update URL
    setTimeout(() => {
      initialLoadCompleteRef.current = true;
    }, 500);
  }, [location.hash]);
  // Update URL hash when key parameters change
  useEffect(() => {
    if (!initialLoadCompleteRef.current) return;
    const hashParams = new URLSearchParams();
    // Add visualization parameters to hash
    hashParams.set('mode', visualizationMode);
    hashParams.set('count', particleCount.toString());
    hashParams.set('speed', particleSpeed.toFixed(1));
    hashParams.set('size', particleSize.toFixed(1));
    hashParams.set('glow', particleGlow.toFixed(1));
    hashParams.set('glowRadius', glowRadius.toFixed(1));
    hashParams.set('glowColor', glowColor);
    hashParams.set('moveAmp', movementAmplification.toFixed(1));
    hashParams.set('sphereDist', sphereDistance.toFixed(1));
    hashParams.set('sphere1Size', sphere1Size.toFixed(1));
    hashParams.set('sphere2Size', sphere2Size.toFixed(1));
    hashParams.set('trails', trailEnabled.toString());
    hashParams.set('trailLength', trailLength.toFixed(2));
    hashParams.set('trailBright', trailBrightness.toFixed(2));
    // Add custom color parameters
    hashParams.set('useCustomColors', useCustomColors.toString());
    hashParams.set('sphere1Color', sphere1Color);
    hashParams.set('sphere2Color', sphere2Color);
    hashParams.set('colorGradientSteps', colorGradientSteps.toString());
    hashParams.set('colorIntensity', colorIntensity.toFixed(1));
    // Explicitly add background color parameter
    hashParams.set('bgColor', backgroundColor);
    // REMOVED: Don't include camera position in regular URL hash updates
    // Only include it when generating embed code
    // Update URL hash without full page reload
    navigate({
      hash: hashParams.toString()
    }, {
      replace: true
    });
  }, [visualizationMode, particleCount, particleSpeed, particleSize, particleGlow, glowRadius, glowColor, movementAmplification, sphereDistance, sphere1Size, sphere2Size, trailEnabled, trailLength, trailBrightness, useCustomColors, sphere1Color, sphere2Color, colorGradientSteps, colorIntensity, backgroundColor,
  // REMOVED: cameraPosition and cameraTarget dependencies
  navigate]);
  const containerRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const controlsRef = useRef<OrbitControls | null>(null);
  const particlesRef = useRef<THREE.Points | null>(null);
  const particlesGeometryRef = useRef<THREE.BufferGeometry | null>(null);
  const particlesMaterialRef = useRef<THREE.ShaderMaterial | null>(null);
  const blochSphere1Ref = useRef<THREE.Group | null>(null);
  const blochSphere2Ref = useRef<THREE.Group | null>(null);
  const stateVector1Ref = useRef<THREE.ArrowHelper | null>(null);
  const stateVector2Ref = useRef<THREE.ArrowHelper | null>(null);
  const frameIdRef = useRef<number | null>(null);
  const clockRef = useRef<THREE.Clock>(new THREE.Clock());
  const centralSphereRef = useRef<THREE.Group | null>(null);
  const centralVectorRef = useRef<THREE.ArrowHelper | null>(null);
  const trailsRef = useRef<THREE.Points | null>(null);
  const trailsGeometryRef = useRef<THREE.BufferGeometry | null>(null);
  const trailsMaterialRef = useRef<THREE.ShaderMaterial | null>(null);
  const particleHistoryRef = useRef<Float32Array[]>([]);
  // Detect changes in emotion data to trigger animations
  useEffect(() => {
    // Store previous emotion values
    if (emotion1 !== prevEmotion1Ref.current || emotion2 !== prevEmotion2Ref.current) {
      prevEmotion1Ref.current = {
        ...emotion1
      };
      prevEmotion2Ref.current = {
        ...emotion2
      };
      // Start animation transition
      animationProgressRef.current = 0.0;
    }
  }, [emotion1, emotion2]);
  // Trigger reinitialize when particle count, visualization mode, or trail settings change
  useEffect(() => {
    setNeedsReinitialize(true);
  }, [particleCount, visualizationMode, trailEnabled]);
  // Update visualization when controls change (without full reinitialization)
  useEffect(() => {
    if (particlesMaterialRef.current) {
      // Update shader uniforms for glow and size
      particlesMaterialRef.current.uniforms.size.value = 0.4 * particleSize;
      particlesMaterialRef.current.uniforms.glowIntensity.value = particleGlow;
      particlesMaterialRef.current.uniforms.glowRadius.value = glowRadius;
      // Convert hex color to THREE.Color
      const color = new THREE.Color(glowColor);
      particlesMaterialRef.current.uniforms.glowColor.value = color;
    }
    if (trailsMaterialRef.current && trailEnabled) {
      trailsMaterialRef.current.uniforms.trailBrightness.value = trailBrightness;
      // Convert hex color to THREE.Color for trail glow
      const color = new THREE.Color(glowColor);
      trailsMaterialRef.current.uniforms.glowColor.value = color;
    }
  }, [particleSize, particleSpeed, particleGlow, glowRadius, glowColor, trailBrightness]);
  // Update sphere positions when distance changes
  useEffect(() => {
    if (blochSphere1Ref.current && blochSphere2Ref.current) {
      const halfDistance = sphereDistance / 2;
      blochSphere1Ref.current.position.x = -halfDistance;
      blochSphere2Ref.current.position.x = halfDistance;
    }
  }, [sphereDistance]);
  // Update sphere sizes
  useEffect(() => {
    if (blochSphere1Ref.current) {
      blochSphere1Ref.current.scale.set(sphere1Size, sphere1Size, sphere1Size);
    }
    if (blochSphere2Ref.current) {
      blochSphere2Ref.current.scale.set(sphere2Size, sphere2Size, sphere2Size);
    }
  }, [sphere1Size, sphere2Size]);
  // Helper function to safely clean up the scene
  const cleanupScene = () => {
    if (frameIdRef.current) {
      cancelAnimationFrame(frameIdRef.current);
      frameIdRef.current = null;
    }
    if (containerRef.current && rendererRef.current && rendererRef.current.domElement) {
      // Check if the DOM element is actually a child before removing
      if (containerRef.current.contains(rendererRef.current.domElement)) {
        containerRef.current.removeChild(rendererRef.current.domElement);
      }
    }
    // Dispose of Three.js resources
    if (rendererRef.current) {
      rendererRef.current.dispose();
      rendererRef.current = null;
    }
    if (particlesGeometryRef.current) {
      particlesGeometryRef.current.dispose();
      particlesGeometryRef.current = null;
    }
    if (particlesMaterialRef.current) {
      particlesMaterialRef.current.dispose();
      particlesMaterialRef.current = null;
    }
    if (trailsGeometryRef.current) {
      trailsGeometryRef.current.dispose();
      trailsGeometryRef.current = null;
    }
    if (trailsMaterialRef.current) {
      trailsMaterialRef.current.dispose();
      trailsMaterialRef.current = null;
    }
    // Reset all refs
    sceneRef.current = null;
    particlesRef.current = null;
    trailsRef.current = null;
    blochSphere1Ref.current = null;
    blochSphere2Ref.current = null;
    stateVector1Ref.current = null;
    stateVector2Ref.current = null;
    centralSphereRef.current = null;
    centralVectorRef.current = null;
    controlsRef.current = null;
    particleHistoryRef.current = [];
  };
  // Initialize the scene
  useEffect(() => {
    if (!containerRef.current) return;
    // Clean up existing scene if reinitializing
    if (sceneRef.current && needsReinitialize) {
      cleanupScene();
      setNeedsReinitialize(false);
    }
    if (sceneRef.current) return;
    const width = containerRef.current.clientWidth;
    const height = containerRef.current.clientHeight;
    // Create scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(backgroundColor);
    sceneRef.current = scene;
    // Apply background color to container element for consistent coloring
    if (containerRef.current) {
      containerRef.current.style.backgroundColor = backgroundColor;
    }
    // Create camera
    const camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 1000);
    // Use stored camera position if available
    camera.position.set(cameraPosition.x, cameraPosition.y, cameraPosition.z);
    cameraRef.current = camera;
    // Create renderer
    const renderer = new THREE.WebGLRenderer({
      antialias: true
    });
    renderer.setSize(width, height);
    renderer.setPixelRatio(window.devicePixelRatio);
    containerRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;
    // Add orbit controls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.rotateSpeed = 0.5;
    controls.zoomSpeed = 0.7;
    controls.panSpeed = 0.8;
    controls.minDistance = 5;
    controls.maxDistance = 150;
    // Check if in embedded mode
    const isEmbedded = location.hash.includes('embedded=true');
    controls.enableZoom = !isEmbedded; // Disable zoom for embedded mode
    controls.enablePan = true;
    // Set target if stored
    if (cameraTarget.x !== 0 || cameraTarget.y !== 0 || cameraTarget.z !== 0) {
      controls.target.set(cameraTarget.x, cameraTarget.y, cameraTarget.z);
      controls.update();
    }
    controlsRef.current = controls;
    // Calculate half distance for sphere positioning
    const halfDistance = sphereDistance / 2;
    // Create Bloch spheres
    createBlochSphere(scene, -halfDistance, 0, 0, true); // First sphere
    createBlochSphere(scene, halfDistance, 0, 0, false); // Second sphere
    // Create central sphere for entangled fusion mode
    if (visualizationMode === 'entangledFusion') {
      createCentralSphere(scene);
    }
    // Create particle system
    createParticleSystem(scene);
    // Create trails system if enabled
    if (trailEnabled) {
      createTrailSystem(scene);
    }
    // Add ambient light
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.3);
    scene.add(ambientLight);
    // Add directional light
    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.7);
    directionalLight.position.set(1, 1, 1);
    scene.add(directionalLight);
    // Add hemisphere light for better overall illumination
    const hemisphereLight = new THREE.HemisphereLight(0xffffbb, 0x080820, 0.5);
    scene.add(hemisphereLight);
    // Start animation
    const animate = () => {
      frameIdRef.current = requestAnimationFrame(animate);
      if (controlsRef.current) {
        controlsRef.current.update();
      }
      // Gently rotate the Bloch spheres
      if (blochSphere1Ref.current) {
        blochSphere1Ref.current.rotation.y += 0.001;
      }
      if (blochSphere2Ref.current) {
        blochSphere2Ref.current.rotation.y += 0.001;
      }
      // Rotate central sphere if it exists
      if (centralSphereRef.current) {
        centralSphereRef.current.rotation.y += 0.001;
      }
      // Progress animation transitions
      if (animationProgressRef.current < 1.0) {
        animationProgressRef.current = Math.min(1.0, animationProgressRef.current + 0.03);
      }
      // Continuously update particle positions for constant motion
      updateParticles();
      // Update time uniform for shaders
      if (particlesMaterialRef.current) {
        particlesMaterialRef.current.uniforms.time.value = clockRef.current.getElapsedTime();
      }
      if (trailsMaterialRef.current) {
        trailsMaterialRef.current.uniforms.time.value = clockRef.current.getElapsedTime();
      }
      if (rendererRef.current && sceneRef.current && cameraRef.current) {
        rendererRef.current.render(sceneRef.current, cameraRef.current);
      }
    };
    animate();
    clockRef.current.start();
    // Handle window resize
    const handleResize = () => {
      if (!containerRef.current || !cameraRef.current || !rendererRef.current) return;
      const newWidth = containerRef.current.clientWidth;
      const newHeight = containerRef.current.clientHeight;
      cameraRef.current.aspect = newWidth / newHeight;
      cameraRef.current.updateProjectionMatrix();
      rendererRef.current.setSize(newWidth, newHeight);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      cleanupScene();
    };
  }, [needsReinitialize, particleCount, visualizationMode, sphereDistance, sphere1Size, sphere2Size, trailEnabled, trailLength, backgroundColor, cameraPosition, cameraTarget, location.hash // Add location.hash as dependency to detect embedded mode
  ]);
  // Update background color when it changes
  useEffect(() => {
    if (sceneRef.current) {
      sceneRef.current.background = new THREE.Color(backgroundColor);
      // Make sure the container background color is also updated
      if (containerRef.current) {
        containerRef.current.style.backgroundColor = backgroundColor;
      }
      // Update particle material blending mode based on background color
      if (particlesMaterialRef.current) {
        const bgColor = new THREE.Color(backgroundColor);
        const isLightBackground = bgColor.r > 0.5 && bgColor.g > 0.5 && bgColor.b > 0.5;
        // Update uniform for shader
        particlesMaterialRef.current.uniforms.isLightBackground.value = isLightBackground ? 1.0 : 0.0;
        // Update blending mode
        particlesMaterialRef.current.blending = isLightBackground ? THREE.NormalBlending : THREE.AdditiveBlending;
        particlesMaterialRef.current.needsUpdate = true;
      }
      // Update trail material blending if it exists
      if (trailsMaterialRef.current) {
        const bgColor = new THREE.Color(backgroundColor);
        const isLightBackground = bgColor.r > 0.5 && bgColor.g > 0.5 && bgColor.b > 0.5;
        // Update uniform for shader
        trailsMaterialRef.current.uniforms.isLightBackground.value = isLightBackground ? 1.0 : 0.0;
        // Update blending mode
        trailsMaterialRef.current.blending = isLightBackground ? THREE.NormalBlending : THREE.AdditiveBlending;
        trailsMaterialRef.current.needsUpdate = true;
      }
    }
  }, [backgroundColor]);
  // Create central sphere for entangled fusion mode
  const createCentralSphere = (scene: THREE.Scene) => {
    const group = new THREE.Group();
    group.position.set(0, 0, 0);
    scene.add(group);
    centralSphereRef.current = group;
    // Create smaller central sphere
    const sphereGeometry = new THREE.SphereGeometry(3, 32, 32);
    const sphereMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      wireframe: true,
      transparent: true,
      opacity: 0.3
    });
    const sphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
    group.add(sphere);
    // Add axes
    const axesHelper = new THREE.AxesHelper(4);
    group.add(axesHelper);
    // Add equator circle
    const equatorGeometry = new THREE.BufferGeometry().setFromPoints(new THREE.Path().absarc(0, 0, 3, 0, Math.PI * 2, true).getPoints(50));
    const equatorMaterial = new THREE.LineBasicMaterial({
      color: 0x444444
    });
    const equator = new THREE.Line(equatorGeometry, equatorMaterial);
    equator.rotation.x = Math.PI / 2;
    group.add(equator);
    // Add state vector arrow
    const dir = new THREE.Vector3(0, 1, 0);
    const origin = new THREE.Vector3(0, 0, 0);
    const length = 3;
    const arrowColor = 0xffffff;
    const stateVector = new THREE.ArrowHelper(dir, origin, length, arrowColor, 0.3, 0.2);
    group.add(stateVector);
    centralVectorRef.current = stateVector;
  };
  // Create Bloch sphere
  const createBlochSphere = (scene: THREE.Scene, x: number, y: number, z: number, isFirst: boolean) => {
    const group = new THREE.Group();
    group.position.set(x, y, z);
    // Apply initial scale based on size factor
    const initialScale = isFirst ? sphere1Size : sphere2Size;
    group.scale.set(initialScale, initialScale, initialScale);
    scene.add(group);
    if (isFirst) {
      blochSphere1Ref.current = group;
    } else {
      blochSphere2Ref.current = group;
    }
    // Create sphere
    const sphereGeometry = new THREE.SphereGeometry(5, 32, 32);
    const sphereColor = isFirst ? useCustomColors ? new THREE.Color(sphere1Color) : new THREE.Color(0xffffff) : useCustomColors ? new THREE.Color(sphere2Color) : new THREE.Color(0xffffff);
    const sphereMaterial = new THREE.MeshBasicMaterial({
      color: sphereColor,
      wireframe: true,
      transparent: true,
      opacity: 0.2
    });
    const sphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
    group.add(sphere);
    // Add axes
    const axesHelper = new THREE.AxesHelper(6);
    group.add(axesHelper);
    // Add equator circle
    const equatorGeometry = new THREE.BufferGeometry().setFromPoints(new THREE.Path().absarc(0, 0, 5, 0, Math.PI * 2, true).getPoints(50));
    const equatorMaterial = new THREE.LineBasicMaterial({
      color: 0x444444
    });
    const equator = new THREE.Line(equatorGeometry, equatorMaterial);
    equator.rotation.x = Math.PI / 2;
    group.add(equator);
    // Add sectors (process stages)
    const sectorNames = ['Perception', 'Novelty', 'Goal-plan', 'Action', 'Progress', 'Result'];
    const sectorColors = [0x00ffff, 0x0000ff, 0xff00ff, 0xff0000, 0xffff00, 0x00ff00];
    for (let i = 0; i < 6; i++) {
      const angle = i * Math.PI / 3;
      const markerX = 5.5 * Math.cos(angle);
      const markerZ = 5.5 * Math.sin(angle);
      const markerGeometry = new THREE.SphereGeometry(0.15, 16, 16);
      const markerMaterial = new THREE.MeshBasicMaterial({
        color: sectorColors[i]
      });
      const marker = new THREE.Mesh(markerGeometry, markerMaterial);
      marker.position.set(markerX, 0, markerZ);
      group.add(marker);
    }
    // Add state vector arrow
    const dir = new THREE.Vector3(0, 1, 0);
    const origin = new THREE.Vector3(0, 0, 0);
    const length = 5;
    const arrowColor = isFirst ? useCustomColors ? new THREE.Color(sphere1Color).getHex() : 0xff0000 : useCustomColors ? new THREE.Color(sphere2Color).getHex() : 0x0000ff;
    const stateVector = new THREE.ArrowHelper(dir, origin, length, arrowColor, 0.5, 0.3);
    group.add(stateVector);
    if (isFirst) {
      stateVector1Ref.current = stateVector;
    } else {
      stateVector2Ref.current = stateVector;
    }
  };
  // Create trail system
  const createTrailSystem = (scene: THREE.Scene) => {
    const maxTrailPoints = particleCount * Math.ceil(trailLength * 20); // Up to 20 points per particle trail
    const trailsGeometry = new THREE.BufferGeometry();
    // Create positions array
    const positions = new Float32Array(maxTrailPoints * 3);
    const colors = new Float32Array(maxTrailPoints * 3);
    const sizes = new Float32Array(maxTrailPoints);
    const opacities = new Float32Array(maxTrailPoints);
    const indices = new Float32Array(maxTrailPoints); // To track which particle each trail point belongs to
    // Initialize all values to zero
    for (let i = 0; i < maxTrailPoints; i++) {
      positions[i * 3] = 0;
      positions[i * 3 + 1] = 0;
      positions[i * 3 + 2] = 0;
      colors[i * 3] = 1;
      colors[i * 3 + 1] = 1;
      colors[i * 3 + 2] = 1;
      sizes[i] = 0.1;
      opacities[i] = 0;
      indices[i] = Math.floor(i / Math.ceil(trailLength * 20)); // Associate with particle index
    }
    trailsGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    trailsGeometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    trailsGeometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));
    trailsGeometry.setAttribute('opacity', new THREE.BufferAttribute(opacities, 1));
    trailsGeometry.setAttribute('index', new THREE.BufferAttribute(indices, 1));
    // Determine if background is light to adjust trail appearance
    const bgColor = new THREE.Color(backgroundColor);
    const isLightBackground = bgColor.r > 0.5 && bgColor.g > 0.5 && bgColor.b > 0.5;
    // Trail shader material
    const trailShaderMaterial = new THREE.ShaderMaterial({
      uniforms: {
        time: {
          value: 0.0
        },
        glowColor: {
          value: new THREE.Color(glowColor)
        },
        trailBrightness: {
          value: trailBrightness
        },
        isLightBackground: {
          value: isLightBackground ? 1.0 : 0.0
        }
      },
      vertexShader: `
        attribute float size;
        attribute float opacity;
        attribute vec3 color;
        varying float vOpacity;
        varying vec3 vColor;
        void main() {
          vOpacity = opacity;
          vColor = color;
          vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
          gl_PointSize = size * (300.0 / -mvPosition.z);
          gl_Position = projectionMatrix * mvPosition;
        }
      `,
      fragmentShader: `
        uniform vec3 glowColor;
        uniform float trailBrightness;
        uniform float isLightBackground;
        varying float vOpacity;
        varying vec3 vColor;
        void main() {
          // Calculate distance from center of point
          vec2 center = gl_PointCoord - 0.5;
          float dist = length(center);
          // Create soft-edged point
          float strength = 1.0 - smoothstep(0.0, 0.5, dist);
          // Blend particle color with glow color
          vec3 finalColor = mix(vColor, glowColor, 0.6);
          // Apply brightness with scaling for high values
          // Use logarithmic scaling for high brightness values to prevent oversaturation
          float scaledBrightness = trailBrightness <= 1.0 ? trailBrightness : 1.0 + log(trailBrightness);
          // Calculate base opacity
          float alpha = vOpacity * strength * scaledBrightness;
          // For light backgrounds, adjust appearance
          if (isLightBackground > 0.5) {
            // Darken colors for visibility
            finalColor = finalColor * 0.8;
            // Increase base opacity
            alpha = max(0.15, alpha);
          }
          gl_FragColor = vec4(finalColor, alpha);
        }
      `,
      transparent: true,
      depthWrite: false,
      blending: isLightBackground ? THREE.NormalBlending : THREE.AdditiveBlending
    });
    const trails = new THREE.Points(trailsGeometry, trailShaderMaterial);
    scene.add(trails);
    trailsGeometryRef.current = trailsGeometry;
    trailsMaterialRef.current = trailShaderMaterial;
    trailsRef.current = trails;
    // Initialize particle history array
    particleHistoryRef.current = [];
    for (let i = 0; i < particleCount; i++) {
      particleHistoryRef.current.push(new Float32Array(Math.ceil(trailLength * 20) * 3));
    }
  };
  // Create particle system with custom shader
  const createParticleSystem = (scene: THREE.Scene) => {
    const currentParticleCount = particleCount;
    const particlesGeometry = new THREE.BufferGeometry();
    // Create positions array
    const positions = new Float32Array(currentParticleCount * 3);
    const colors = new Float32Array(currentParticleCount * 3);
    const sizes = new Float32Array(currentParticleCount);
    const velocities = new Float32Array(currentParticleCount * 3);
    const influences = new Float32Array(currentParticleCount);
    const modes = new Float32Array(currentParticleCount); // For mode-specific behavior
    // Calculate half distance for sphere positioning
    const halfDistance = sphereDistance / 2;
    // Distribute particles based on visualization mode
    switch (visualizationMode) {
      case 'baseline':
        // Original distribution - particles throughout the scene
        for (let i = 0; i < currentParticleCount; i++) {
          // Create a more interesting distribution
          if (Math.random() > 0.5) {
            // Half around the first sphere
            const radius = 5 + Math.random() * 10;
            const theta = Math.random() * Math.PI * 2;
            const phi = Math.random() * Math.PI;
            positions[i * 3] = -halfDistance + radius * Math.sin(phi) * Math.cos(theta);
            positions[i * 3 + 1] = radius * Math.sin(phi) * Math.sin(theta);
            positions[i * 3 + 2] = radius * Math.cos(phi);
            influences[i] = Math.random() * 0.4; // More influenced by first emotion
          } else {
            // Half around the second sphere
            const radius = 5 + Math.random() * 10;
            const theta = Math.random() * Math.PI * 2;
            const phi = Math.random() * Math.PI;
            positions[i * 3] = halfDistance + radius * Math.sin(phi) * Math.cos(theta);
            positions[i * 3 + 1] = radius * Math.sin(phi) * Math.sin(theta);
            positions[i * 3 + 2] = radius * Math.cos(phi);
            influences[i] = 0.6 + Math.random() * 0.4; // More influenced by second emotion
          }
          // Random velocities
          velocities[i * 3] = (Math.random() - 0.5) * 0.05;
          velocities[i * 3 + 1] = (Math.random() - 0.5) * 0.05;
          velocities[i * 3 + 2] = (Math.random() - 0.5) * 0.05;
          // Varied sizes, adjusted by global size factor
          sizes[i] = (0.1 + Math.pow(Math.random(), 2) * 0.8) * particleSize;
          // Default mode data
          modes[i] = Math.random();
        }
        break;
      case 'resonanceTransfer':
        // Particles flow from Sphere A to Sphere B
        for (let i = 0; i < currentParticleCount; i++) {
          // Start most particles near the first sphere
          const radius = 2 + Math.random() * 8;
          const theta = Math.random() * Math.PI * 2;
          const phi = Math.random() * Math.PI;
          positions[i * 3] = -halfDistance + radius * Math.sin(phi) * Math.cos(theta);
          positions[i * 3 + 1] = radius * Math.sin(phi) * Math.sin(theta);
          positions[i * 3 + 2] = radius * Math.cos(phi);
          // Progression along the transfer path (0 to 1)
          influences[i] = Math.random();
          // Mode-specific data: particle lifetime/phase
          modes[i] = Math.random() * Math.PI * 2; // Random phase
          // Initial velocities directed toward sphere B
          const toSphereB = new THREE.Vector3(sphereDistance, 0, 0).normalize().multiplyScalar(0.05);
          velocities[i * 3] = toSphereB.x + (Math.random() - 0.5) * 0.01;
          velocities[i * 3 + 1] = toSphereB.y + (Math.random() - 0.5) * 0.01;
          velocities[i * 3 + 2] = toSphereB.z + (Math.random() - 0.5) * 0.01;
          // Varied sizes
          sizes[i] = (0.1 + Math.pow(Math.random(), 2) * 0.6) * particleSize;
        }
        break;
      case 'interferenceImpact':
        // Two streams colliding from both spheres
        for (let i = 0; i < currentParticleCount; i++) {
          const fromSphereA = i < currentParticleCount / 2;
          if (fromSphereA) {
            // Particles from sphere A
            const radius = 2 + Math.random() * 5;
            const theta = Math.random() * Math.PI * 2;
            const phi = Math.random() * Math.PI;
            positions[i * 3] = -halfDistance + radius * Math.sin(phi) * Math.cos(theta);
            positions[i * 3 + 1] = radius * Math.sin(phi) * Math.sin(theta);
            positions[i * 3 + 2] = radius * Math.cos(phi);
            // Direction toward center
            const toCenter = new THREE.Vector3(halfDistance, 0, 0).normalize().multiplyScalar(0.05);
            velocities[i * 3] = toCenter.x + (Math.random() - 0.5) * 0.01;
            velocities[i * 3 + 1] = toCenter.y + (Math.random() - 0.5) * 0.01;
            velocities[i * 3 + 2] = toCenter.z + (Math.random() - 0.5) * 0.01;
            influences[i] = 0.0; // Influenced by first emotion
          } else {
            // Particles from sphere B
            const radius = 2 + Math.random() * 5;
            const theta = Math.random() * Math.PI * 2;
            const phi = Math.random() * Math.PI;
            positions[i * 3] = halfDistance + radius * Math.sin(phi) * Math.cos(theta);
            positions[i * 3 + 1] = radius * Math.sin(phi) * Math.sin(theta);
            positions[i * 3 + 2] = radius * Math.cos(phi);
            // Direction toward center
            const toCenter = new THREE.Vector3(-halfDistance, 0, 0).normalize().multiplyScalar(0.05);
            velocities[i * 3] = toCenter.x + (Math.random() - 0.5) * 0.01;
            velocities[i * 3 + 1] = toCenter.y + (Math.random() - 0.5) * 0.01;
            velocities[i * 3 + 2] = toCenter.z + (Math.random() - 0.5) * 0.01;
            influences[i] = 1.0; // Influenced by second emotion
          }
          // Mode-specific data: interference pattern phase
          modes[i] = Math.random();
          // Varied sizes
          sizes[i] = (0.1 + Math.pow(Math.random(), 2) * 0.6) * particleSize;
        }
        break;
      case 'entangledFusion':
        // Three streams: from A, from B, and a central merged stream
        for (let i = 0; i < currentParticleCount; i++) {
          if (i < currentParticleCount / 3) {
            // Stream from sphere A toward center
            const radius = 2 + Math.random() * 5;
            const theta = Math.random() * Math.PI * 2;
            const phi = Math.random() * Math.PI;
            positions[i * 3] = -halfDistance + radius * Math.sin(phi) * Math.cos(theta);
            positions[i * 3 + 1] = radius * Math.sin(phi) * Math.sin(theta);
            positions[i * 3 + 2] = radius * Math.cos(phi);
            // Direction toward center
            const toCenter = new THREE.Vector3(halfDistance, 0, 0).normalize().multiplyScalar(0.04);
            velocities[i * 3] = toCenter.x + (Math.random() - 0.5) * 0.01;
            velocities[i * 3 + 1] = toCenter.y + (Math.random() - 0.5) * 0.01;
            velocities[i * 3 + 2] = toCenter.z + (Math.random() - 0.5) * 0.01;
            influences[i] = 0.0; // From sphere A
            modes[i] = 0.0; // Stream type: from A
          } else if (i < 2 * (currentParticleCount / 3)) {
            // Stream from sphere B toward center
            const radius = 2 + Math.random() * 5;
            const theta = Math.random() * Math.PI * 2;
            const phi = Math.random() * Math.PI;
            positions[i * 3] = halfDistance + radius * Math.sin(phi) * Math.cos(theta);
            positions[i * 3 + 1] = radius * Math.sin(phi) * Math.sin(theta);
            positions[i * 3 + 2] = radius * Math.cos(phi);
            // Direction toward center
            const toCenter = new THREE.Vector3(-halfDistance, 0, 0).normalize().multiplyScalar(0.04);
            velocities[i * 3] = toCenter.x + (Math.random() - 0.5) * 0.01;
            velocities[i * 3 + 1] = toCenter.y + (Math.random() - 0.5) * 0.01;
            velocities[i * 3 + 2] = toCenter.z + (Math.random() - 0.5) * 0.01;
            influences[i] = 1.0; // From sphere B
            modes[i] = 1.0; // Stream type: from B
          } else {
            // Central entangled stream
            const radius = 1 + Math.random() * 3;
            const theta = Math.random() * Math.PI * 2;
            const phi = Math.random() * Math.PI;
            positions[i * 3] = radius * Math.sin(phi) * Math.cos(theta);
            positions[i * 3 + 1] = radius * Math.sin(phi) * Math.sin(theta);
            positions[i * 3 + 2] = radius * Math.cos(phi);
            // Random velocities for central stream
            velocities[i * 3] = (Math.random() - 0.5) * 0.02;
            velocities[i * 3 + 1] = (Math.random() - 0.5) * 0.02;
            velocities[i * 3 + 2] = (Math.random() - 0.5) * 0.02;
            influences[i] = 0.5; // Blend of both emotions
            modes[i] = 2.0; // Stream type: central
          }
          // Varied sizes
          sizes[i] = (0.1 + Math.pow(Math.random(), 2) * 0.8) * particleSize;
        }
        break;
      default:
        // Default distribution (similar to original)
        for (let i = 0; i < currentParticleCount; i++) {
          // Distribute particles throughout the scene
          if (Math.random() > 0.5) {
            // Half around the first sphere
            const radius = 5 + Math.random() * 10;
            const theta = Math.random() * Math.PI * 2;
            const phi = Math.random() * Math.PI;
            positions[i * 3] = -halfDistance + radius * Math.sin(phi) * Math.cos(theta);
            positions[i * 3 + 1] = radius * Math.sin(phi) * Math.sin(theta);
            positions[i * 3 + 2] = radius * Math.cos(phi);
            influences[i] = Math.random() * 0.3; // More influenced by first emotion
          } else {
            // Half around the second sphere
            const radius = 5 + Math.random() * 10;
            const theta = Math.random() * Math.PI * 2;
            const phi = Math.random() * Math.PI;
            positions[i * 3] = halfDistance + radius * Math.sin(phi) * Math.cos(theta);
            positions[i * 3 + 1] = radius * Math.sin(phi) * Math.sin(theta);
            positions[i * 3 + 2] = radius * Math.cos(phi);
            influences[i] = 0.7 + Math.random() * 0.3; // More influenced by second emotion
          }
          // Random velocities
          velocities[i * 3] = (Math.random() - 0.5) * 0.03;
          velocities[i * 3 + 1] = (Math.random() - 0.5) * 0.03;
          velocities[i * 3 + 2] = (Math.random() - 0.5) * 0.03;
          // Varied sizes, adjusted by global size factor
          sizes[i] = (0.1 + Math.pow(Math.random(), 2) * 0.8) * particleSize;
          // Default mode data
          modes[i] = Math.random();
        }
    }
    // Initial colors (will be updated in the animation loop)
    for (let i = 0; i < currentParticleCount; i++) {
      colors[i * 3] = 1;
      colors[i * 3 + 1] = 1;
      colors[i * 3 + 2] = 1;
    }
    particlesGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    particlesGeometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    particlesGeometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));
    // Store velocities and influences for animation
    particlesGeometry.userData = {
      velocities,
      influences,
      modes,
      originalPositions: positions.slice()
    };
    particlesGeometryRef.current = particlesGeometry;
    // Determine if background is light to adjust particle appearance
    const bgColor = new THREE.Color(backgroundColor);
    const isLightBackground = bgColor.r > 0.5 && bgColor.g > 0.5 && bgColor.b > 0.5;
    // Create particle material with custom shader for better looking particles
    const customShaderMaterial = new THREE.ShaderMaterial({
      uniforms: {
        time: {
          value: 0.0
        },
        size: {
          value: 0.4 * particleSize
        },
        glowIntensity: {
          value: particleGlow
        },
        glowRadius: {
          value: glowRadius
        },
        glowColor: {
          value: new THREE.Color(glowColor)
        },
        isLightBackground: {
          value: isLightBackground ? 1.0 : 0.0
        }
      },
      vertexShader: `
        attribute float size;
        attribute vec3 color;
        varying vec3 vColor;
        uniform float time;
        void main() {
          vColor = color;
          vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
          gl_PointSize = size * (300.0 / -mvPosition.z);
          gl_Position = projectionMatrix * mvPosition;
        }
      `,
      fragmentShader: `
        varying vec3 vColor;
        uniform float glowIntensity;
        uniform float glowRadius;
        uniform vec3 glowColor;
        uniform float isLightBackground;
        void main() {
          // Calculate distance from center of point
          vec2 center = gl_PointCoord - 0.5;
          float dist = length(center);
          // Create soft-edged point
          float strength = 1.0 - smoothstep(0.0, 0.5, dist);
          // Blend particle color with glow color
          vec3 finalColor = mix(vColor, glowColor, 0.6);
          // Scale intensity for high values using logarithmic scaling
          float scaledIntensity = glowIntensity <= 5.0 ? glowIntensity : 5.0 + log(glowIntensity - 4.0) * 2.0;
          // Apply scaled glow intensity to opacity
          float alpha = strength * (scaledIntensity * 0.6);
          // For light backgrounds, add contrast and opacity
          if (isLightBackground > 0.5) {
            // Darken colors for visibility on light backgrounds
            finalColor = finalColor * 0.8;
            // Add dark edge/outline for contrast
            float edge = 1.0 - smoothstep(0.45, 0.5, dist);
            if (edge < 0.1) {
              finalColor = vec3(0.0, 0.0, 0.0);
            }
            // Increase opacity for light backgrounds
            alpha = max(0.2, alpha);
          }
          gl_FragColor = vec4(finalColor, alpha);
        }
      `,
      transparent: true,
      depthWrite: false,
      blending: isLightBackground ? THREE.NormalBlending : THREE.AdditiveBlending
    });
    // Create particle system
    const particles = new THREE.Points(particlesGeometry, customShaderMaterial);
    scene.add(particles);
    particlesRef.current = particles;
    particlesMaterialRef.current = customShaderMaterial;
  };
  // Enhanced sector color function with more dramatic colors
  const getSectorColor = (sector: string, intensity: number = 1.0): THREE.Color => {
    const sectorColorMap = {
      Perception: new THREE.Color(0x00ffff),
      Novelty: new THREE.Color(0x0000ff),
      'Goal-plan': new THREE.Color(0xff00ff),
      Action: new THREE.Color(0xff0000),
      Progress: new THREE.Color(0xffff00),
      Result: new THREE.Color(0x00ff00) // Green
    };
    // Get base color and enhance it
    const baseColor = sectorColorMap[sector] || new THREE.Color(0xffffff);
    // Apply intensity to make colors more vibrant
    const enhancedColor = baseColor.clone();
    // Make colors more saturated for dramatic effect
    enhancedColor.r = Math.pow(enhancedColor.r, 0.7) * intensity;
    enhancedColor.g = Math.pow(enhancedColor.g, 0.7) * intensity;
    enhancedColor.b = Math.pow(enhancedColor.b, 0.7) * intensity;
    return enhancedColor;
  };
  // Generate colors for the gradient between spheres
  const generateGradientColors = (steps: number) => {
    const color1 = new THREE.Color(sphere1Color);
    const color2 = new THREE.Color(sphere2Color);
    const colors = [];
    for (let i = 0; i < steps; i++) {
      const t = i / (steps - 1);
      const color = new THREE.Color().copy(color1).lerp(color2, t);
      colors.push(color);
    }
    return colors;
  };
  // Calculate color based on position between spheres
  const getColorFromPosition = (position: THREE.Vector3, center1: THREE.Vector3, center2: THREE.Vector3) => {
    const dist1 = position.distanceTo(center1);
    const dist2 = position.distanceTo(center2);
    const totalDist = dist1 + dist2;
    // Calculate normalized position along the gradient (0 = at sphere1, 1 = at sphere2)
    let t = dist1 / totalDist;
    // Apply some non-linear mapping to make the gradient more interesting
    t = Math.pow(t, 0.8); // Adjust power for different gradient distribution
    if (useCustomColors) {
      const gradientColors = generateGradientColors(colorGradientSteps);
      // Find the two closest colors in our gradient
      const index = Math.min(Math.floor(t * (gradientColors.length - 1)), gradientColors.length - 2);
      const localT = t * (gradientColors.length - 1) - index;
      // Interpolate between the two closest colors
      const color = new THREE.Color().copy(gradientColors[index]).lerp(gradientColors[index + 1], localT);
      // Apply intensity multiplier
      color.r = Math.min(1, color.r * colorIntensity);
      color.g = Math.min(1, color.g * colorIntensity);
      color.b = Math.min(1, color.b * colorIntensity);
      return color;
    } else {
      // Fall back to original emotion-based coloring if custom colors are disabled
      return null;
    }
  };
  // Update particles - separated function for continuous motion
  const updateParticles = () => {
    if (!particlesGeometryRef.current) return;
    const geometry = particlesGeometryRef.current;
    const positions = geometry.attributes.position.array as Float32Array;
    const colors = geometry.attributes.color.array as Float32Array;
    const sizes = geometry.attributes.size.array as Float32Array;
    const velocities = geometry.userData.velocities as Float32Array;
    const influences = geometry.userData.influences as Float32Array;
    const modes = geometry.userData.modes as Float32Array;
    // Get current and previous emotion data for animation
    const currentEmotion1 = emotion1;
    const currentEmotion2 = emotion2;
    const prevEmotion1 = prevEmotion1Ref.current;
    const prevEmotion2 = prevEmotion2Ref.current;
    const animationProgress = animationProgressRef.current;
    // Interpolate between previous and current emotion values
    const lerpEmotionData = (prev: EmotionData, current: EmotionData, t: number) => {
      // For phi, we need to handle the circular nature (0 to 2π)
      let phiDiff = current.phi - prev.phi;
      // Handle wrapping around 2π
      if (phiDiff > Math.PI) phiDiff -= 2 * Math.PI;
      if (phiDiff < -Math.PI) phiDiff += 2 * Math.PI;
      const lerpedPhi = prev.phi + phiDiff * t;
      // Interpolate color values
      const lerpedColor = {
        r: prev.color.r + (current.color.r - prev.color.r) * t,
        g: prev.color.g + (current.color.g - prev.color.g) * t,
        b: prev.color.b + (current.color.b - prev.color.b) * t
      };
      // Interpolate Stokes vector
      const lerpedStokes = {
        x: prev.stokesVector.x + (current.stokesVector.x - prev.stokesVector.x) * t,
        y: prev.stokesVector.y + (current.stokesVector.y - prev.stokesVector.y) * t,
        z: prev.stokesVector.z + (current.stokesVector.z - prev.stokesVector.z) * t
      };
      return {
        ...current,
        phi: lerpedPhi,
        theta: prev.theta + (current.theta - prev.theta) * t,
        color: lerpedColor,
        stokesVector: lerpedStokes
      };
    };
    // Interpolate emotion data for smooth transitions
    const interpolatedEmotion1 = lerpEmotionData(prevEmotion1, currentEmotion1, animationProgress);
    const interpolatedEmotion2 = lerpEmotionData(prevEmotion2, currentEmotion2, animationProgress);
    // Get emotion colors with enhanced saturation for more dramatic effect
    const colorIntensity = 1.5 * (1 + particleGlow * 0.5); // Boost intensity with glow
    const color1 = new THREE.Color(interpolatedEmotion1.color.r * colorIntensity, interpolatedEmotion1.color.g * colorIntensity, interpolatedEmotion1.color.b * colorIntensity);
    const color2 = new THREE.Color(interpolatedEmotion2.color.r * colorIntensity, interpolatedEmotion2.color.g * colorIntensity, interpolatedEmotion2.color.b * colorIntensity);
    // Get sector colors for more vibrant particles
    const sectorColor1 = getSectorColor(interpolatedEmotion1.sector, 1.5 * (1 + particleGlow * 0.3));
    const sectorColor2 = getSectorColor(interpolatedEmotion2.sector, 1.5 * (1 + particleGlow * 0.3));
    // Calculate emotion intensity factors
    const intensity1 = Math.sqrt(interpolatedEmotion1.stokesVector.x ** 2 + interpolatedEmotion1.stokesVector.y ** 2 + interpolatedEmotion1.stokesVector.z ** 2) * sphere1Size; // Scale intensity by sphere size
    const intensity2 = Math.sqrt(interpolatedEmotion2.stokesVector.x ** 2 + interpolatedEmotion2.stokesVector.y ** 2 + interpolatedEmotion2.stokesVector.z ** 2) * sphere2Size; // Scale intensity by sphere size
    // Calculate flow direction based on emotional states
    const emotionVector1 = new THREE.Vector3(interpolatedEmotion1.stokesVector.x, interpolatedEmotion1.stokesVector.z, interpolatedEmotion1.stokesVector.y).normalize();
    const emotionVector2 = new THREE.Vector3(interpolatedEmotion2.stokesVector.x, interpolatedEmotion2.stokesVector.z, interpolatedEmotion2.stokesVector.y).normalize();
    // Calculate half distance for sphere positioning
    const halfDistance = sphereDistance / 2;
    // Define the centers of the Bloch spheres
    const center1 = new THREE.Vector3(-halfDistance, 0, 0);
    const center2 = new THREE.Vector3(halfDistance, 0, 0);
    const centerPoint = new THREE.Vector3(0, 0, 0);
    // Get time for animation effects
    const time = clockRef.current.getElapsedTime();
    // Apply global speed factor and movement amplification
    const speedFactor = particleSpeed * movementAmplification;
    // Update central vector in entangled fusion mode
    if (visualizationMode === 'entangledFusion' && centralVectorRef.current) {
      // Calculate a blended vector based on both emotion states
      const blendedVector = new THREE.Vector3().copy(emotionVector1).multiplyScalar(intensity1).add(emotionVector2.clone().multiplyScalar(intensity2)).normalize();
      centralVectorRef.current.setDirection(blendedVector);
      // Create a blended color
      const blendedColor = new THREE.Color().copy(color1).lerp(color2, 0.5);
      centralVectorRef.current.setColor(blendedColor);
    }
    // Update trails if enabled
    if (trailEnabled && trailsGeometryRef.current) {
      const trailsGeometry = trailsGeometryRef.current;
      const trailPositions = trailsGeometry.attributes.position.array as Float32Array;
      const trailColors = trailsGeometry.attributes.color.array as Float32Array;
      const trailSizes = trailsGeometry.attributes.size.array as Float32Array;
      const trailOpacities = trailsGeometry.attributes.opacity.array as Float32Array;
      const maxTrailLength = Math.ceil(trailLength * 20); // Max points per trail
      // Update each particle's trail history
      for (let i = 0; i < particleCount; i++) {
        const history = particleHistoryRef.current[i];
        // Shift history points
        for (let j = history.length - 3; j >= 3; j -= 3) {
          history[j] = history[j - 3];
          history[j + 1] = history[j - 2];
          history[j + 2] = history[j - 1];
        }
        // Add current position to history
        history[0] = positions[i * 3];
        history[1] = positions[i * 3 + 1];
        history[2] = positions[i * 3 + 2];
        // Update trail points
        for (let j = 0; j < maxTrailLength; j++) {
          const trailIndex = i * maxTrailLength + j;
          if (j < history.length / 3) {
            // Position
            trailPositions[trailIndex * 3] = history[j * 3];
            trailPositions[trailIndex * 3 + 1] = history[j * 3 + 1];
            trailPositions[trailIndex * 3 + 2] = history[j * 3 + 2];
            // Color - get the particle's current color
            trailColors[trailIndex * 3] = colors[i * 3];
            trailColors[trailIndex * 3 + 1] = colors[i * 3 + 1];
            trailColors[trailIndex * 3 + 2] = colors[i * 3 + 2];
            // Size - decrease along the trail
            const sizeFactor = 1.0 - j / maxTrailLength;
            trailSizes[trailIndex] = sizes[i] * sizeFactor * 0.8;
            // Opacity - decrease along the trail
            trailOpacities[trailIndex] = (1.0 - j / maxTrailLength) * 0.7;
          } else {
            // Hide unused trail points
            trailPositions[trailIndex * 3] = 0;
            trailPositions[trailIndex * 3 + 1] = 0;
            trailPositions[trailIndex * 3 + 2] = 0;
            trailOpacities[trailIndex] = 0;
          }
        }
      }
      // Update the geometry attributes
      trailsGeometry.attributes.position.needsUpdate = true;
      trailsGeometry.attributes.color.needsUpdate = true;
      trailsGeometry.attributes.size.needsUpdate = true;
      trailsGeometry.attributes.opacity.needsUpdate = true;
    }
    // Update each particle based on the visualization mode
    switch (visualizationMode) {
      case 'baseline':
        // Original particle behavior with enhanced visuals
        for (let i = 0; i < positions.length / 3; i++) {
          // Get current position
          const x = positions[i * 3];
          const y = positions[i * 3 + 1];
          const z = positions[i * 3 + 2];
          const currentPos = new THREE.Vector3(x, y, z);
          // Calculate influence factor (how much each emotion affects this particle)
          const influence = influences[i];
          // Dynamic influence based on particle position and time
          const dynamicInfluence = (influence + Math.sin(time * 0.5 + i * 0.01)) * 0.5;
          // Calculate distances to centers
          const dist1 = currentPos.distanceTo(center1);
          const dist2 = currentPos.distanceTo(center2);
          const distToInteraction = currentPos.distanceTo(centerPoint);
          // Check if we should use custom gradient colors
          if (useCustomColors) {
            // Get color from position in the gradient
            const gradientColor = getColorFromPosition(currentPos, center1, center2);
            colors[i * 3] = gradientColor.r;
            colors[i * 3 + 1] = gradientColor.g;
            colors[i * 3 + 2] = gradientColor.b;
          } else {
            // Calculate weights based on distances and influences (original behavior)
            let weight1, weight2;
            if (distToInteraction < sphereDistance / 3) {
              // Particles in the interaction zone are influenced by both emotions
              weight1 = (1 - dynamicInfluence) * intensity1;
              weight2 = dynamicInfluence * intensity2;
            } else if (dist1 < dist2) {
              // Closer to first sphere
              weight1 = (1 - 0.7 * (dist1 / (10 * sphere1Size))) * intensity1;
              weight2 = Math.max(0, 1 - dist2 / 30) * intensity2 * 0.3;
            } else {
              // Closer to second sphere
              weight1 = Math.max(0, 1 - dist1 / 30) * intensity1 * 0.3;
              weight2 = (1 - 0.7 * (dist2 / (10 * sphere2Size))) * intensity2;
            }
            const totalWeight = weight1 + weight2;
            const normalizedWeight1 = totalWeight > 0 ? weight1 / totalWeight : 0.5;
            const normalizedWeight2 = totalWeight > 0 ? weight2 / totalWeight : 0.5;
            // Blend colors based on influence and emotion colors with enhanced vibrancy
            const emotionBlend1 = new THREE.Color().copy(color1).lerp(sectorColor1, 0.7); // More sector color influence for vibrancy
            const emotionBlend2 = new THREE.Color().copy(color2).lerp(sectorColor2, 0.7); // More sector color influence for vibrancy
            // Final color blend with enhanced saturation
            const blendedColor = new THREE.Color().copy(emotionBlend1).multiplyScalar(normalizedWeight1).add(emotionBlend2.clone().multiplyScalar(normalizedWeight2));
            // Brighten colors for more vibrant display - enhanced by glow factor
            const glowBoost = 1.5 * (1 + particleGlow * 0.5);
            blendedColor.r = Math.min(1, blendedColor.r * glowBoost);
            blendedColor.g = Math.min(1, blendedColor.g * glowBoost);
            blendedColor.b = Math.min(1, blendedColor.b * glowBoost);
            // Update color
            colors[i * 3] = blendedColor.r;
            colors[i * 3 + 1] = blendedColor.g;
            colors[i * 3 + 2] = blendedColor.b;
          }
          // Calculate size based on emotional intensity and position
          let sizeFactor;
          if (distToInteraction < sphereDistance / 3) {
            // Particles in interaction zone are larger
            sizeFactor = 1.5 + (intensity1 + intensity2) * 0.5;
          } else {
            // Size decreases with distance from relevant sphere
            const relevantDist = dist1 < dist2 ? dist1 : dist2;
            const relevantSize = dist1 < dist2 ? sphere1Size : sphere2Size;
            sizeFactor = 1 + Math.max(0, 1 - relevantDist / (20 * relevantSize)) * (intensity1 + intensity2) * 0.5;
          }
          // Apply global size factor and glow effect
          const glowSizeFactor = 1 + particleGlow * 0.7;
          sizes[i] = Math.max(0.1, Math.min(1.5 * glowSizeFactor, (0.1 + Math.random() * 0.3) * sizeFactor * particleSize * glowSizeFactor));
          // Calculate flow forces
          let flowForce = new THREE.Vector3();
          if (distToInteraction < sphereDistance / 3) {
            // In interaction zone - complex interaction between emotions
            const emotionInteraction = new THREE.Vector3().copy(emotionVector1).multiplyScalar(intensity1).add(emotionVector2.clone().multiplyScalar(intensity2)).normalize().multiplyScalar(0.03 * speedFactor);
            // Add circular motion in interaction zone
            const angle = time * 0.5 + i * 0.01;
            const circularMotion = new THREE.Vector3(Math.cos(angle) * 0.02 * speedFactor, Math.sin(angle * 0.7) * 0.01 * speedFactor, Math.sin(angle) * 0.02 * speedFactor);
            flowForce.add(emotionInteraction).add(circularMotion);
          } else {
            // Flow between spheres based on position and emotional states
            const toCenter1 = new THREE.Vector3().subVectors(center1, currentPos).normalize();
            const toCenter2 = new THREE.Vector3().subVectors(center2, currentPos).normalize();
            const toInteraction = new THREE.Vector3().subVectors(centerPoint, currentPos).normalize();
            // Particles flow toward interaction center with some probability
            if (Math.random() < 0.02) {
              flowForce.copy(toInteraction).multiplyScalar(0.02 * speedFactor);
            } else if (dist1 < dist2) {
              // Flow around first sphere with some influence from second
              const orbitForce = new THREE.Vector3(-toCenter1.z * 0.02 * speedFactor, (Math.random() - 0.5) * 0.01 * speedFactor, toCenter1.x * 0.02 * speedFactor);
              flowForce.copy(orbitForce).add(toCenter2.clone().multiplyScalar(0.005 * intensity2 * speedFactor)).add(emotionVector1.clone().multiplyScalar(0.01 * intensity1 * speedFactor));
            } else {
              // Flow around second sphere with some influence from first
              const orbitForce = new THREE.Vector3(-toCenter2.z * 0.02 * speedFactor, (Math.random() - 0.5) * 0.01 * speedFactor, toCenter2.x * 0.02 * speedFactor);
              flowForce.copy(orbitForce).add(toCenter1.clone().multiplyScalar(0.005 * intensity1 * speedFactor)).add(emotionVector2.clone().multiplyScalar(0.01 * intensity2 * speedFactor));
            }
          }
          // Add noise for more natural movement
          const noiseX = Math.sin(time + i * 0.1) * 0.002 * speedFactor;
          const noiseY = Math.cos(time + i * 0.1) * 0.002 * speedFactor;
          const noiseZ = Math.sin(time * 0.5 + i * 0.1) * 0.002 * speedFactor;
          flowForce.x += noiseX;
          flowForce.y += noiseY;
          flowForce.z += noiseZ;
          // Add constant motion regardless of other factors
          flowForce.x += Math.sin(time * 0.2 + i * 0.01) * 0.003 * speedFactor;
          flowForce.y += Math.cos(time * 0.3 + i * 0.02) * 0.002 * speedFactor;
          flowForce.z += Math.sin(time * 0.25 + i * 0.015) * 0.003 * speedFactor;
          // Update velocity with damping and flow forces
          velocities[i * 3] = velocities[i * 3] * 0.95 + flowForce.x;
          velocities[i * 3 + 1] = velocities[i * 3 + 1] * 0.95 + flowForce.y;
          velocities[i * 3 + 2] = velocities[i * 3 + 2] * 0.95 + flowForce.z;
          // Apply velocity to position
          positions[i * 3] += velocities[i * 3];
          positions[i * 3 + 1] += velocities[i * 3 + 1];
          positions[i * 3 + 2] += velocities[i * 3 + 2];
          // Contain particles within the scene
          const maxDistance = Math.max(50, sphereDistance * 1.5); // Adjust containment based on sphere distance
          const distFromOrigin = Math.sqrt(x * x + y * y + z * z);
          if (distFromOrigin > maxDistance) {
            const containmentForce = 0.02;
            positions[i * 3] -= positions[i * 3] / distFromOrigin * containmentForce;
            positions[i * 3 + 1] -= positions[i * 3 + 1] / distFromOrigin * containmentForce;
            positions[i * 3 + 2] -= positions[i * 3 + 2] / distFromOrigin * containmentForce;
          }
        }
        break;
      case 'resonanceTransfer':
        // Particles flow from Sphere A to Sphere B, carrying emotion state
        for (let i = 0; i < positions.length / 3; i++) {
          // Get current position
          const x = positions[i * 3];
          const y = positions[i * 3 + 1];
          const z = positions[i * 3 + 2];
          const currentPos = new THREE.Vector3(x, y, z);
          // Check if we should use custom gradient colors
          if (useCustomColors) {
            // Get color from position in the gradient
            const gradientColor = getColorFromPosition(currentPos, center1, center2);
            colors[i * 3] = gradientColor.r;
            colors[i * 3 + 1] = gradientColor.g;
            colors[i * 3 + 2] = gradientColor.b;
          } else {
            // Original color logic
            // Calculate distances to spheres
            const dist1 = currentPos.distanceTo(center1);
            const dist2 = currentPos.distanceTo(center2);
            // Update influence as particles move from sphere A to B
            // Closer to sphere B = more influenced by emotion1 (carrying it over)
            const transferProgress = Math.min(1, dist1 / sphereDistance);
            influences[i] = transferProgress;
            // Update phase for ribbon-like effect
            modes[i] += 0.02 * speedFactor;
            // Calculate ribbon position in the transfer path
            const pathProgress = (time * 0.1 + modes[i]) % 1;
            // Color based on position in transfer (carrying emotion1 color to sphere B)
            // Enhanced by glow
            const emotionBlend1 = new THREE.Color().copy(color1).lerp(sectorColor1, 0.3);
            // Apply glow effect to color
            const glowBoost = 1 + particleGlow * 0.5;
            emotionBlend1.r = Math.min(1, emotionBlend1.r * glowBoost);
            emotionBlend1.g = Math.min(1, emotionBlend1.g * glowBoost);
            emotionBlend1.b = Math.min(1, emotionBlend1.b * glowBoost);
            colors[i * 3] = emotionBlend1.r;
            colors[i * 3 + 1] = emotionBlend1.g;
            colors[i * 3 + 2] = emotionBlend1.b;
          }
          // Calculate distances to spheres
          const dist1 = currentPos.distanceTo(center1);
          const dist2 = currentPos.distanceTo(center2);
          // Update influence as particles move from sphere A to B
          // Closer to sphere B = more influenced by emotion1 (carrying it over)
          const transferProgress = Math.min(1, dist1 / sphereDistance);
          influences[i] = transferProgress;
          // Update phase for ribbon-like effect
          modes[i] += 0.02 * speedFactor;
          // Calculate ribbon position in the transfer path
          const pathProgress = (time * 0.1 + modes[i]) % 1;
          const pathOffset = Math.sin(pathProgress * Math.PI * 4) * 3;
          // Calculate target position along transfer path
          const targetX = -halfDistance + sphereDistance * pathProgress;
          const targetY = pathOffset * Math.sin(time * 0.2 + i * 0.01);
          const targetZ = pathOffset * Math.cos(time * 0.3 + i * 0.01);
          const target = new THREE.Vector3(targetX, targetY, targetZ);
          // Move toward target position
          const toTarget = new THREE.Vector3().subVectors(target, currentPos).normalize().multiplyScalar(0.2 * speedFactor);
          // Add some noise for more natural movement
          const noiseX = Math.sin(time + i * 0.1) * 0.01 * speedFactor;
          const noiseY = Math.cos(time + i * 0.1) * 0.01 * speedFactor;
          const noiseZ = Math.sin(time * 0.5 + i * 0.1) * 0.01 * speedFactor;
          // Update velocity
          velocities[i * 3] = velocities[i * 3] * 0.9 + toTarget.x + noiseX;
          velocities[i * 3 + 1] = velocities[i * 3 + 1] * 0.9 + toTarget.y + noiseY;
          velocities[i * 3 + 2] = velocities[i * 3 + 2] * 0.9 + toTarget.z + noiseZ;
          // Apply velocity to position
          positions[i * 3] += velocities[i * 3];
          positions[i * 3 + 1] += velocities[i * 3 + 1];
          positions[i * 3 + 2] += velocities[i * 3 + 2];
          // Size pulsates along the ribbon - enhanced by glow
          const sizePulse = 0.8 + 0.4 * Math.sin(pathProgress * Math.PI * 8);
          const glowSizeFactor = 1 + particleGlow * 0.7;
          sizes[i] = 0.2 * sizePulse * particleSize * glowSizeFactor;
          // Reset particles that reach sphere B
          if (dist2 < 5 * sphere2Size) {
            // Reset to near sphere A
            const radius = 2 + Math.random() * 5;
            const theta = Math.random() * Math.PI * 2;
            const phi = Math.random() * Math.PI;
            positions[i * 3] = -halfDistance + radius * Math.sin(phi) * Math.cos(theta) * sphere1Size;
            positions[i * 3 + 1] = radius * Math.sin(phi) * Math.sin(theta) * sphere1Size;
            positions[i * 3 + 2] = radius * Math.cos(phi) * sphere1Size;
            // Reset phase
            modes[i] = Math.random() * Math.PI * 2;
          }
        }
        break;
      case 'interferenceImpact':
        // Two streams colliding and creating interference patterns
        for (let i = 0; i < positions.length / 3; i++) {
          // Get current position
          const x = positions[i * 3];
          const y = positions[i * 3 + 1];
          const z = positions[i * 3 + 2];
          const currentPos = new THREE.Vector3(x, y, z);
          // Calculate distances
          const dist1 = currentPos.distanceTo(center1);
          const dist2 = currentPos.distanceTo(center2);
          const distToCenter = currentPos.distanceTo(centerPoint);
          // Determine if this particle is in the collision zone
          const inCollisionZone = distToCenter < sphereDistance / 4;
          // Original influence (0 = from sphere A, 1 = from sphere B)
          const originalInfluence = influences[i];
          if (inCollisionZone) {
            // In collision zone - create interference patterns
            const interference = Math.sin(time * 2 + x * 0.5 + y * 0.3 + z * 0.2);
            const constructiveInterference = (interference + 1) * 0.5; // 0 to 1
            // Blend colors based on interference pattern
            const emotionBlend1 = new THREE.Color().copy(color1).lerp(sectorColor1, 0.3);
            const emotionBlend2 = new THREE.Color().copy(color2).lerp(sectorColor2, 0.3);
            // Constructive interference = brighter blend, destructive = darker
            const blendedColor = new THREE.Color().copy(emotionBlend1).lerp(emotionBlend2, constructiveInterference);
            // Apply glow effect to color - more pronounced in constructive interference
            const glowBoost = 1 + particleGlow * 0.5 * (1 + constructiveInterference);
            // Brighten for constructive interference
            if (constructiveInterference > 0.7) {
              blendedColor.r = Math.min(1, blendedColor.r * 1.5 * glowBoost);
              blendedColor.g = Math.min(1, blendedColor.g * 1.5 * glowBoost);
              blendedColor.b = Math.min(1, blendedColor.b * 1.5 * glowBoost);
            } else {
              blendedColor.r = Math.min(1, blendedColor.r * glowBoost);
              blendedColor.g = Math.min(1, blendedColor.g * glowBoost);
              blendedColor.b = Math.min(1, blendedColor.b * glowBoost);
            }
            colors[i * 3] = blendedColor.r;
            colors[i * 3 + 1] = blendedColor.g;
            colors[i * 3 + 2] = blendedColor.b;
            // Size varies with interference and glow
            const glowSizeFactor = 1 + particleGlow * 0.7;
            sizes[i] = (0.1 + constructiveInterference * 0.4) * particleSize * glowSizeFactor;
            // Chaotic motion in collision zone
            const chaosFactor = 0.03 * speedFactor;
            velocities[i * 3] += (Math.random() - 0.5) * chaosFactor;
            velocities[i * 3 + 1] += (Math.random() - 0.5) * chaosFactor;
            velocities[i * 3 + 2] += (Math.random() - 0.5) * chaosFactor;
            // Slow down in collision zone
            velocities[i * 3] *= 0.95;
            velocities[i * 3 + 1] *= 0.95;
            velocities[i * 3 + 2] *= 0.95;
          } else {
            // Outside collision zone - move toward center
            if (originalInfluence < 0.5) {
              // From sphere A
              const emotionBlend1 = new THREE.Color().copy(color1).lerp(sectorColor1, 0.3);
              // Apply glow effect
              const glowBoost = 1 + particleGlow * 0.5;
              emotionBlend1.r = Math.min(1, emotionBlend1.r * glowBoost);
              emotionBlend1.g = Math.min(1, emotionBlend1.g * glowBoost);
              emotionBlend1.b = Math.min(1, emotionBlend1.b * glowBoost);
              colors[i * 3] = emotionBlend1.r;
              colors[i * 3 + 1] = emotionBlend1.g;
              colors[i * 3 + 2] = emotionBlend1.b;
              // Move toward center
              const toCenter = new THREE.Vector3().subVectors(centerPoint, currentPos).normalize().multiplyScalar(0.05 * speedFactor);
              velocities[i * 3] = velocities[i * 3] * 0.95 + toCenter.x;
              velocities[i * 3 + 1] = velocities[i * 3 + 1] * 0.95 + toCenter.y;
              velocities[i * 3 + 2] = velocities[i * 3 + 2] * 0.95 + toCenter.z;
            } else {
              // From sphere B
              const emotionBlend2 = new THREE.Color().copy(color2).lerp(sectorColor2, 0.3);
              // Apply glow effect
              const glowBoost = 1 + particleGlow * 0.5;
              emotionBlend2.r = Math.min(1, emotionBlend2.r * glowBoost);
              emotionBlend2.g = Math.min(1, emotionBlend2.g * glowBoost);
              emotionBlend2.b = Math.min(1, emotionBlend2.b * glowBoost);
              colors[i * 3] = emotionBlend2.r;
              colors[i * 3 + 1] = emotionBlend2.g;
              colors[i * 3 + 2] = emotionBlend2.b;
              // Move toward center
              const toCenter = new THREE.Vector3().subVectors(centerPoint, currentPos).normalize().multiplyScalar(0.05 * speedFactor);
              velocities[i * 3] = velocities[i * 3] * 0.95 + toCenter.x;
              velocities[i * 3 + 1] = velocities[i * 3 + 1] * 0.95 + toCenter.y;
              velocities[i * 3 + 2] = velocities[i * 3 + 2] * 0.95 + toCenter.z;
            }
            // Standard size with glow effect
            const glowSizeFactor = 1 + particleGlow * 0.7;
            sizes[i] = 0.2 * particleSize * glowSizeFactor;
          }
          // Apply velocity
          positions[i * 3] += velocities[i * 3];
          positions[i * 3 + 1] += velocities[i * 3 + 1];
          positions[i * 3 + 2] += velocities[i * 3 + 2];
          // Reset particles that go too far
          if (dist1 > sphereDistance && dist2 > sphereDistance) {
            // Reset to original sphere
            if (originalInfluence < 0.5) {
              // Reset to sphere A
              const radius = 2 + Math.random() * 5;
              const theta = Math.random() * Math.PI * 2;
              const phi = Math.random() * Math.PI;
              positions[i * 3] = -halfDistance + radius * Math.sin(phi) * Math.cos(theta) * sphere1Size;
              positions[i * 3 + 1] = radius * Math.sin(phi) * Math.sin(theta) * sphere1Size;
              positions[i * 3 + 2] = radius * Math.cos(phi) * sphere1Size;
              // Reset velocity toward center
              const toCenter = new THREE.Vector3(halfDistance, 0, 0).normalize().multiplyScalar(0.05);
              velocities[i * 3] = toCenter.x + (Math.random() - 0.5) * 0.01;
              velocities[i * 3 + 1] = toCenter.y + (Math.random() - 0.5) * 0.01;
              velocities[i * 3 + 2] = toCenter.z + (Math.random() - 0.5) * 0.01;
            } else {
              // Reset to sphere B
              const radius = 2 + Math.random() * 5;
              const theta = Math.random() * Math.PI * 2;
              const phi = Math.random() * Math.PI;
              positions[i * 3] = halfDistance + radius * Math.sin(phi) * Math.cos(theta) * sphere2Size;
              positions[i * 3 + 1] = radius * Math.sin(phi) * Math.sin(theta) * sphere2Size;
              positions[i * 3 + 2] = radius * Math.cos(phi) * sphere2Size;
              // Reset velocity toward center
              const toCenter = new THREE.Vector3(-halfDistance, 0, 0).normalize().multiplyScalar(0.05);
              velocities[i * 3] = toCenter.x + (Math.random() - 0.5) * 0.01;
              velocities[i * 3 + 1] = toCenter.y + (Math.random() - 0.5) * 0.01;
              velocities[i * 3 + 2] = toCenter.z + (Math.random() - 0.5) * 0.01;
            }
          }
        }
        break;
      case 'entangledFusion':
        // Three streams: from A, from B, and a central merged stream
        for (let i = 0; i < positions.length / 3; i++) {
          // Get current position
          const x = positions[i * 3];
          const y = positions[i * 3 + 1];
          const z = positions[i * 3 + 2];
          const currentPos = new THREE.Vector3(x, y, z);
          // Get stream type from mode data
          // 0 = from A, 1 = from B, 2 = central
          const streamType = Math.round(modes[i]);
          // Calculate distances
          const dist1 = currentPos.distanceTo(center1);
          const dist2 = currentPos.distanceTo(center2);
          const distToCenter = currentPos.distanceTo(centerPoint);
          // Apply glow factor to all particles
          const glowSizeFactor = 1 + particleGlow * 0.7;
          const glowColorBoost = 1 + particleGlow * 0.5;
          if (streamType === 0) {
            // Stream from A to center
            const emotionBlend1 = new THREE.Color().copy(color1).lerp(sectorColor1, 0.3);
            // Apply glow effect
            emotionBlend1.r = Math.min(1, emotionBlend1.r * glowColorBoost);
            emotionBlend1.g = Math.min(1, emotionBlend1.g * glowColorBoost);
            emotionBlend1.b = Math.min(1, emotionBlend1.b * glowColorBoost);
            colors[i * 3] = emotionBlend1.r;
            colors[i * 3 + 1] = emotionBlend1.g;
            colors[i * 3 + 2] = emotionBlend1.b;
            // Flow toward center
            if (distToCenter > 3) {
              const toCenter = new THREE.Vector3().subVectors(centerPoint, currentPos).normalize().multiplyScalar(0.04 * speedFactor);
              velocities[i * 3] = velocities[i * 3] * 0.95 + toCenter.x;
              velocities[i * 3 + 1] = velocities[i * 3 + 1] * 0.95 + toCenter.y;
              velocities[i * 3 + 2] = velocities[i * 3 + 2] * 0.95 + toCenter.z;
              // Helix effect
              const angle = time * 0.5 + i * 0.01;
              const helixRadius = 2 * (1 - distToCenter / sphereDistance);
              velocities[i * 3] += Math.cos(angle) * 0.01 * helixRadius * speedFactor;
              velocities[i * 3 + 2] += Math.sin(angle) * 0.01 * helixRadius * speedFactor;
            } else {
              // Convert to central stream
              modes[i] = 2.0;
            }
            // Size with glow effect
            sizes[i] = 0.2 * particleSize * glowSizeFactor;
            // Reset if too far
            if (dist1 > sphereDistance && distToCenter > sphereDistance / 3) {
              // Reset to sphere A
              const radius = 2 + Math.random() * 5;
              const theta = Math.random() * Math.PI * 2;
              const phi = Math.random() * Math.PI;
              positions[i * 3] = -halfDistance + radius * Math.sin(phi) * Math.cos(theta) * sphere1Size;
              positions[i * 3 + 1] = radius * Math.sin(phi) * Math.sin(theta) * sphere1Size;
              positions[i * 3 + 2] = radius * Math.cos(phi) * sphere1Size;
              // Reset velocity toward center
              const toCenter = new THREE.Vector3(halfDistance, 0, 0).normalize().multiplyScalar(0.04);
              velocities[i * 3] = toCenter.x + (Math.random() - 0.5) * 0.01;
              velocities[i * 3 + 1] = toCenter.y + (Math.random() - 0.5) * 0.01;
              velocities[i * 3 + 2] = toCenter.z + (Math.random() - 0.5) * 0.01;
            }
          } else if (streamType === 1) {
            // Stream from B to center
            const emotionBlend2 = new THREE.Color().copy(color2).lerp(sectorColor2, 0.3);
            // Apply glow effect
            emotionBlend2.r = Math.min(1, emotionBlend2.r * glowColorBoost);
            emotionBlend2.g = Math.min(1, emotionBlend2.g * glowColorBoost);
            emotionBlend2.b = Math.min(1, emotionBlend2.b * glowColorBoost);
            colors[i * 3] = emotionBlend2.r;
            colors[i * 3 + 1] = emotionBlend2.g;
            colors[i * 3 + 2] = emotionBlend2.b;
            // Flow toward center
            if (distToCenter > 3) {
              const toCenter = new THREE.Vector3().subVectors(centerPoint, currentPos).normalize().multiplyScalar(0.04 * speedFactor);
              velocities[i * 3] = velocities[i * 3] * 0.95 + toCenter.x;
              velocities[i * 3 + 1] = velocities[i * 3 + 1] * 0.95 + toCenter.y;
              velocities[i * 3 + 2] = velocities[i * 3 + 2] * 0.95 + toCenter.z;
              // Helix effect (opposite direction)
              const angle = time * 0.5 + i * 0.01;
              const helixRadius = 2 * (1 - distToCenter / sphereDistance);
              velocities[i * 3] += Math.cos(angle + Math.PI) * 0.01 * helixRadius * speedFactor;
              velocities[i * 3 + 2] += Math.sin(angle + Math.PI) * 0.01 * helixRadius * speedFactor;
            } else {
              // Convert to central stream
              modes[i] = 2.0;
            }
            // Size with glow effect
            sizes[i] = 0.2 * particleSize * glowSizeFactor;
            // Reset if too far
            if (dist2 > sphereDistance && distToCenter > sphereDistance / 3) {
              // Reset to sphere B
              const radius = 2 + Math.random() * 5;
              const theta = Math.random() * Math.PI * 2;
              const phi = Math.random() * Math.PI;
              positions[i * 3] = halfDistance + radius * Math.sin(phi) * Math.cos(theta) * sphere2Size;
              positions[i * 3 + 1] = radius * Math.sin(phi) * Math.sin(theta) * sphere2Size;
              positions[i * 3 + 2] = radius * Math.cos(phi) * sphere2Size;
              // Reset velocity toward center
              const toCenter = new THREE.Vector3(-halfDistance, 0, 0).normalize().multiplyScalar(0.04);
              velocities[i * 3] = toCenter.x + (Math.random() - 0.5) * 0.01;
              velocities[i * 3 + 1] = toCenter.y + (Math.random() - 0.5) * 0.01;
              velocities[i * 3 + 2] = toCenter.z + (Math.random() - 0.5) * 0.01;
            }
          } else {
            // Central entangled stream
            // Blend colors for central stream
            const emotionBlend1 = new THREE.Color().copy(color1).lerp(sectorColor1, 0.3);
            const emotionBlend2 = new THREE.Color().copy(color2).lerp(sectorColor2, 0.3);
            const blendFactor = 0.5 + 0.3 * Math.sin(time + i * 0.1);
            const blendedColor = new THREE.Color().copy(emotionBlend1).lerp(emotionBlend2, blendFactor);
            // Brighten the central stream with glow effect
            // Central stream gets extra glow boost
            const centralGlowBoost = 1.3 * (1 + particleGlow * 0.7);
            blendedColor.r = Math.min(1, blendedColor.r * centralGlowBoost);
            blendedColor.g = Math.min(1, blendedColor.g * centralGlowBoost);
            blendedColor.b = Math.min(1, blendedColor.b * centralGlowBoost);
            colors[i * 3] = blendedColor.r;
            colors[i * 3 + 1] = blendedColor.g;
            colors[i * 3 + 2] = blendedColor.b;
            // Orbit around center
            const orbitSpeed = 0.03 * speedFactor;
            const orbitRadius = 3;
            // Calculate orbit forces
            const toCenter = new THREE.Vector3().subVectors(centerPoint, currentPos).normalize();
            const orbitForce = new THREE.Vector3(-toCenter.z * orbitSpeed, (Math.random() - 0.5) * 0.01 * speedFactor, toCenter.x * orbitSpeed);
            // Add some vertical motion based on the blended emotion vectors
            const blendedVector = new THREE.Vector3().copy(emotionVector1).multiplyScalar(intensity1).add(emotionVector2.clone().multiplyScalar(intensity2)).normalize().multiplyScalar(0.01 * speedFactor);
            velocities[i * 3] = velocities[i * 3] * 0.95 + orbitForce.x + blendedVector.x;
            velocities[i * 3 + 1] = velocities[i * 3 + 1] * 0.95 + orbitForce.y + blendedVector.y;
            velocities[i * 3 + 2] = velocities[i * 3 + 2] * 0.95 + orbitForce.z + blendedVector.z;
            // Larger size for central stream with enhanced glow
            const centralSizeFactor = glowSizeFactor * 1.2;
            sizes[i] = (0.2 + 0.1 * Math.sin(time * 2 + i * 0.1)) * particleSize * centralSizeFactor;
            // Contain within central sphere
            if (distToCenter > orbitRadius) {
              const containForce = 0.02 * speedFactor;
              const toCenter = new THREE.Vector3().subVectors(centerPoint, currentPos).normalize().multiplyScalar(containForce);
              velocities[i * 3] += toCenter.x;
              velocities[i * 3 + 1] += toCenter.y;
              velocities[i * 3 + 2] += toCenter.z;
            }
            // Occasionally convert back to A or B stream
            if (Math.random() < 0.001) {
              if (Math.random() < 0.5) {
                modes[i] = 0.0; // Convert to A stream
                // Position at edge of central sphere
                const dir = new THREE.Vector3(Math.random() - 0.5, Math.random() - 0.5, Math.random() - 0.5).normalize();
                positions[i * 3] = dir.x * 3;
                positions[i * 3 + 1] = dir.y * 3;
                positions[i * 3 + 2] = dir.z * 3;
                // Velocity toward sphere A
                const toSphereA = new THREE.Vector3(-halfDistance, 0, 0).sub(new THREE.Vector3(positions[i * 3], positions[i * 3 + 1], positions[i * 3 + 2])).normalize().multiplyScalar(0.04);
                velocities[i * 3] = toSphereA.x;
                velocities[i * 3 + 1] = toSphereA.y;
                velocities[i * 3 + 2] = toSphereA.z;
              } else {
                modes[i] = 1.0; // Convert to B stream
                // Position at edge of central sphere
                const dir = new THREE.Vector3(Math.random() - 0.5, Math.random() - 0.5, Math.random() - 0.5).normalize();
                positions[i * 3] = dir.x * 3;
                positions[i * 3 + 1] = dir.y * 3;
                positions[i * 3 + 2] = dir.z * 3;
                // Velocity toward sphere B
                const toSphereB = new THREE.Vector3(halfDistance, 0, 0).sub(new THREE.Vector3(positions[i * 3], positions[i * 3 + 1], positions[i * 3 + 2])).normalize().multiplyScalar(0.04);
                velocities[i * 3] = toSphereB.x;
                velocities[i * 3 + 1] = toSphereB.y;
                velocities[i * 3 + 2] = toSphereB.z;
              }
            }
          }
          // Apply velocity
          positions[i * 3] += velocities[i * 3];
          positions[i * 3 + 1] += velocities[i * 3 + 1];
          positions[i * 3 + 2] += velocities[i * 3 + 2];
        }
        break;
    }
    // Update the geometry attributes
    geometry.attributes.position.needsUpdate = true;
    geometry.attributes.color.needsUpdate = true;
    geometry.attributes.size.needsUpdate = true;
  };
  // Update visualization based on emotions
  useEffect(() => {
    if (!stateVector1Ref.current || !stateVector2Ref.current) return;
    // Get current and previous emotion data for animation
    const currentEmotion1 = emotion1;
    const currentEmotion2 = emotion2;
    const prevEmotion1 = prevEmotion1Ref.current;
    const prevEmotion2 = prevEmotion2Ref.current;
    const animationProgress = animationProgressRef.current;
    // Interpolate between previous and current emotion values for smooth transitions
    const lerpAngle = (start: number, end: number, t: number) => {
      // Handle wrapping around 2π
      let diff = end - start;
      if (diff > Math.PI) diff -= 2 * Math.PI;
      if (diff < -Math.PI) diff += 2 * Math.PI;
      return start + diff * t;
    };
    // Interpolate theta and phi for smooth rotation
    const theta1 = prevEmotion1.theta + (currentEmotion1.theta - prevEmotion1.theta) * animationProgress;
    const phi1 = lerpAngle(prevEmotion1.phi, currentEmotion1.phi, animationProgress);
    // Update state vector 1
    const x1 = Math.sin(theta1) * Math.cos(phi1);
    const y1 = Math.cos(theta1);
    const z1 = Math.sin(theta1) * Math.sin(phi1);
    const direction1 = new THREE.Vector3(x1, y1, z1);
    direction1.normalize();
    stateVector1Ref.current.setDirection(direction1);
    // Interpolate color for smooth transition
    const r1 = prevEmotion1.color.r + (currentEmotion1.color.r - prevEmotion1.color.r) * animationProgress;
    const g1 = prevEmotion1.color.g + (currentEmotion1.color.g - prevEmotion1.color.g) * animationProgress;
    const b1 = prevEmotion1.color.b + (currentEmotion1.color.b - prevEmotion1.color.b) * animationProgress;
    const color1 = new THREE.Color(r1, g1, b1);
    stateVector1Ref.current.setColor(color1);
    // Interpolate theta and phi for smooth rotation
    const theta2 = prevEmotion2.theta + (currentEmotion2.theta - prevEmotion2.theta) * animationProgress;
    const phi2 = lerpAngle(prevEmotion2.phi, currentEmotion2.phi, animationProgress);
    // Update state vector 2
    const x2 = Math.sin(theta2) * Math.cos(phi2);
    const y2 = Math.cos(theta2);
    const z2 = Math.sin(theta2) * Math.sin(phi2);
    const direction2 = new THREE.Vector3(x2, y2, z2);
    direction2.normalize();
    stateVector2Ref.current.setDirection(direction2);
    // Interpolate color for smooth transition
    const r2 = prevEmotion2.color.r + (currentEmotion2.color.r - prevEmotion2.color.r) * animationProgress;
    const g2 = prevEmotion2.color.g + (currentEmotion2.color.g - prevEmotion2.color.g) * animationProgress;
    const b2 = prevEmotion2.color.b + (currentEmotion2.color.b - prevEmotion2.color.b) * animationProgress;
    const color2 = new THREE.Color(r2, g2, b2);
    stateVector2Ref.current.setColor(color2);
    // Update central vector in entangled fusion mode
    if (visualizationMode === 'entangledFusion' && centralVectorRef.current) {
      // Calculate a blended vector based on both emotion states
      const blendedVector = new THREE.Vector3().copy(direction1).multiplyScalar(0.5 * sphere1Size).add(direction2.clone().multiplyScalar(0.5 * sphere2Size)).normalize();
      centralVectorRef.current.setDirection(blendedVector);
      // Create a blended color
      const blendedColor = new THREE.Color().copy(color1).lerp(color2, 0.5);
      centralVectorRef.current.setColor(blendedColor);
    }
  }, [emotion1, emotion2, visualizationMode, sphere1Size, sphere2Size]);
  // Handle control changes
  const handleSpeedChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setParticleSpeed(parseFloat(e.target.value));
  };
  const handleSizeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setParticleSize(parseFloat(e.target.value));
  };
  const handleGlowChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setParticleGlow(parseFloat(e.target.value));
  };
  const handleGlowRadiusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setGlowRadius(parseFloat(e.target.value));
  };
  const handleGlowColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setGlowColor(e.target.value);
  };
  const handleTrailToggle = () => {
    setTrailEnabled(!trailEnabled);
  };
  const handleTrailLengthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTrailLength(parseFloat(e.target.value));
  };
  const handleTrailBrightnessChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTrailBrightness(parseFloat(e.target.value));
  };
  const handleCountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setParticleCount(parseInt(e.target.value, 10));
  };
  const handleSphereDistanceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSphereDistance(parseFloat(e.target.value));
  };
  const handleSphere1SizeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSphere1Size(parseFloat(e.target.value));
  };
  const handleSphere2SizeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSphere2Size(parseFloat(e.target.value));
  };
  const handleMovementAmplificationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMovementAmplification(parseFloat(e.target.value));
  };
  // Handle background color change
  const handleBackgroundColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBackgroundColor(e.target.value);
  };
  // Handle color changes
  const handleSphere1ColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSphere1Color(e.target.value);
  };
  const handleSphere2ColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSphere2Color(e.target.value);
  };
  const handleUseCustomColorsToggle = () => {
    setUseCustomColors(!useCustomColors);
  };
  const handleColorGradientStepsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setColorGradientSteps(parseInt(e.target.value));
  };
  const handleColorIntensityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setColorIntensity(parseFloat(e.target.value));
  };
  // Handle mode changes
  const handleModeChange = (mode: VisualizationMode) => {
    setVisualizationMode(mode);
  };
  // Share the current view configuration
  const handleShareView = () => {
    // Create a new URLSearchParams with current parameters
    const hashParams = new URLSearchParams();
    // Add visualization parameters to hash
    hashParams.set('mode', visualizationMode);
    hashParams.set('count', particleCount.toString());
    hashParams.set('speed', particleSpeed.toFixed(1));
    hashParams.set('size', particleSize.toFixed(1));
    hashParams.set('glow', particleGlow.toFixed(1));
    hashParams.set('glowRadius', glowRadius.toFixed(1));
    hashParams.set('glowColor', glowColor);
    hashParams.set('moveAmp', movementAmplification.toFixed(1));
    hashParams.set('sphereDist', sphereDistance.toFixed(1));
    hashParams.set('sphere1Size', sphere1Size.toFixed(1));
    hashParams.set('sphere2Size', sphere2Size.toFixed(1));
    hashParams.set('trails', trailEnabled.toString());
    hashParams.set('trailLength', trailLength.toFixed(2));
    hashParams.set('trailBright', trailBrightness.toFixed(2));
    // Add custom color parameters
    hashParams.set('useCustomColors', useCustomColors.toString());
    hashParams.set('sphere1Color', sphere1Color);
    hashParams.set('sphere2Color', sphere2Color);
    hashParams.set('colorGradientSteps', colorGradientSteps.toString());
    hashParams.set('colorIntensity', colorIntensity.toFixed(1));
    // Explicitly add background color parameter
    hashParams.set('bgColor', backgroundColor);
    // Add emotion parameters
    hashParams.set('theta1', emotion1.theta.toString());
    hashParams.set('phi1', emotion1.phi.toString());
    hashParams.set('theta2', emotion2.theta.toString());
    hashParams.set('phi2', emotion2.phi.toString());
    // Get the base URL without hash
    const baseUrl = window.location.href.split('#')[0];
    const shareUrl = `${baseUrl}#${hashParams.toString()}`;
    // Create a temporary input element to copy the URL to clipboard
    const tempInput = document.createElement('input');
    document.body.appendChild(tempInput);
    tempInput.value = shareUrl;
    tempInput.select();
    document.execCommand('copy');
    document.body.removeChild(tempInput);
    // Show custom alert
    setAlertMessage('Link copied to clipboard');
    setShowAlert(true);
    // Hide alert after 3 seconds
    setTimeout(() => {
      setShowAlert(false);
    }, 3000);
  };
  // Toggle panel visibility
  const toggleInfoPanel = () => setInfoPanelVisible(!infoPanelVisible);
  const toggleModePanel = () => setModePanelVisible(!modePanelVisible);
  const toggleControlPanel = () => setControlPanelVisible(!controlPanelVisible);
  const toggleHintPanel = () => setHintVisible(!hintVisible);
  // Toggle all panels at once (for mobile cleanup)
  const toggleAllPanels = () => {
    const newState = !infoPanelVisible;
    setInfoPanelVisible(newState);
    setModePanelVisible(newState);
    setControlPanelVisible(newState);
    setHintVisible(newState);
  };
  // Generate embed code for the current view
  const generateEmbedCode = () => {
    // Capture current camera position and target ONLY when requested
    let currentCamPosition = {
      x: 0,
      y: 15,
      z: 40
    };
    let currentCamTarget = {
      x: 0,
      y: 0,
      z: 0
    };
    // Get current camera position directly from refs
    if (cameraRef.current) {
      currentCamPosition = {
        x: cameraRef.current.position.x,
        y: cameraRef.current.position.y,
        z: cameraRef.current.position.z
      };
    }
    // Get current camera target directly from controls
    if (controlsRef.current) {
      currentCamTarget = {
        x: controlsRef.current.target.x,
        y: controlsRef.current.target.y,
        z: controlsRef.current.target.z
      };
    }
    // Get current hash parameters for visualization settings
    const hashParams = new URLSearchParams();
    // Add visualization parameters to hash
    hashParams.set('mode', visualizationMode);
    hashParams.set('count', particleCount.toString());
    hashParams.set('speed', particleSpeed.toFixed(1));
    hashParams.set('size', particleSize.toFixed(1));
    hashParams.set('glow', particleGlow.toFixed(1));
    hashParams.set('glowRadius', glowRadius.toFixed(1));
    hashParams.set('glowColor', glowColor);
    hashParams.set('moveAmp', movementAmplification.toFixed(1));
    hashParams.set('sphereDist', sphereDistance.toFixed(1));
    hashParams.set('sphere1Size', sphere1Size.toFixed(1));
    hashParams.set('sphere2Size', sphere2Size.toFixed(1));
    hashParams.set('trails', trailEnabled.toString());
    hashParams.set('trailLength', trailLength.toFixed(2));
    hashParams.set('trailBright', trailBrightness.toFixed(2));
    // Add custom color parameters
    hashParams.set('useCustomColors', useCustomColors.toString());
    hashParams.set('sphere1Color', sphere1Color);
    hashParams.set('sphere2Color', sphere2Color);
    hashParams.set('colorGradientSteps', colorGradientSteps.toString());
    hashParams.set('colorIntensity', colorIntensity.toFixed(1));
    // Make sure background color is included in the hash
    hashParams.set('bgColor', backgroundColor);
    // Add camera position parameters - ONLY in the embed code
    hashParams.set('camX', currentCamPosition.x.toFixed(2));
    hashParams.set('camY', currentCamPosition.y.toFixed(2));
    hashParams.set('camZ', currentCamPosition.z.toFixed(2));
    // Add camera target parameters - ONLY in the embed code
    hashParams.set('targetX', currentCamTarget.x.toFixed(2));
    hashParams.set('targetY', currentCamTarget.y.toFixed(2));
    hashParams.set('targetZ', currentCamTarget.z.toFixed(2));
    // Add emotion parameters
    hashParams.set('theta1', emotion1.theta.toString());
    hashParams.set('phi1', emotion1.phi.toString());
    hashParams.set('theta2', emotion2.theta.toString());
    hashParams.set('phi2', emotion2.phi.toString());
    // Special parameter to indicate embedded mode (no controls)
    hashParams.set('embedded', 'true');
    // Get the base URL without hash
    const baseUrl = window.location.href.split('#')[0];
    const embedUrl = `${baseUrl}#${hashParams.toString()}`;
    // Create iframe HTML code with explicit styling for background color
    const iframeCode = `<iframe src="${embedUrl}" width="100%" height="100%" style="height:100vh; width:100%; border:none; background-color:${backgroundColor};" frameborder="0" scrolling="no" allow="fullscreen"></iframe>`;
    return iframeCode;
  };
  // Handle embed button click
  const handleEmbedView = () => {
    const code = generateEmbedCode();
    setEmbedCode(code);
    setShowEmbedModal(true);
  };
  // Copy embed code to clipboard
  const copyEmbedCode = () => {
    try {
      // Try the modern clipboard API first
      navigator.clipboard.writeText(embedCode).then(() => {
        setAlertMessage('Embed code copied to clipboard');
        setShowAlert(true);
        setTimeout(() => {
          setShowAlert(false);
        }, 3000);
      }).catch(err => {
        console.error('Failed to copy using Clipboard API:', err);
        // Fallback method using a temporary textarea element
        fallbackCopyToClipboard(embedCode);
      });
    } catch (err) {
      console.error('Copy operation error:', err);
      // Use fallback if the primary method fails
      fallbackCopyToClipboard(embedCode);
    }
  };
  // Fallback clipboard method
  const fallbackCopyToClipboard = (text: string) => {
    try {
      // Create a temporary textarea element
      const textArea = document.createElement('textarea');
      textArea.value = text;
      // Make the textarea out of viewport
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      // Focus and select the text
      textArea.focus();
      textArea.select();
      // Execute the copy command
      const successful = document.execCommand('copy');
      // Remove the temporary element
      document.body.removeChild(textArea);
      if (successful) {
        setAlertMessage('Embed code copied to clipboard');
      } else {
        setAlertMessage('Unable to copy - please select and copy manually');
      }
      setShowAlert(true);
      setTimeout(() => {
        setShowAlert(false);
      }, 3000);
    } catch (err) {
      console.error('Fallback copy failed:', err);
      setAlertMessage('Please select the code and copy manually');
      setShowAlert(true);
      setTimeout(() => {
        setShowAlert(false);
      }, 3000);
    }
  };
  // Close embed modal
  const closeEmbedModal = () => {
    setShowEmbedModal(false);
  };
  return <div className="relative h-full" data-id="element-105">
      <div className={`${location.hash.includes('embedded=true') ? 'h-screen w-full' : 'h-[600px] w-full'} border-2 border-white`} ref={containerRef} style={{
      backgroundColor: backgroundColor
    }} data-id="element-106"></div>
      {/* Check for embedded mode */}
      {!location.hash.includes('embedded=true') && <>
          {/* Toggle all panels button (mobile-friendly) */}
          <button onClick={toggleAllPanels} className="absolute top-2 left-2 z-50 bg-black bg-opacity-70 border border-white text-white px-2 py-1 text-xs" data-id="element-107">
            {infoPanelVisible ? 'HIDE PANELS' : 'SHOW PANELS'}
          </button>
          {/* Share View button */}
          <button onClick={handleShareView} className="absolute top-2 right-14 z-50 bg-black bg-opacity-70 border border-white text-white px-2 py-1 text-xs" data-id="element-108">
            SHARE VIEW
          </button>
          {/* Embed button */}
          <button onClick={handleEmbedView} className="absolute top-2 right-2 z-50 bg-black bg-opacity-70 border border-white text-white px-2 py-1 text-xs" data-id="element-109">
            EMBED
          </button>
          {/* Info panel */}
          <div className={`absolute top-12 left-4 bg-black bg-opacity-70 p-2 border border-white text-xs transition-opacity duration-300 ${infoPanelVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} data-id="element-110">
            <div className="flex justify-between items-center mb-1" data-id="element-111">
              <div className="font-bold" data-id="element-112">QUANTUM EMOTION INTERACTION</div>
              <button onClick={toggleInfoPanel} className="ml-2 w-5 h-5 flex items-center justify-center border border-white" data-id="element-113">
                {infoPanelVisible ? '−' : '+'}
              </button>
            </div>
            <div className="grid grid-cols-2 gap-4" data-id="element-114">
              <div data-id="element-115">
                <div className="text-xs mb-1" data-id="element-116">
                  MAP A: {emotion1.name.toUpperCase()}
                </div>
                <div className="w-4 h-4 border border-white" style={{
              backgroundColor: useCustomColors ? sphere1Color : `rgb(${Math.round(emotion1.color.r * 255)}, ${Math.round(emotion1.color.g * 255)}, ${Math.round(emotion1.color.b * 255)})`
            }} data-id="element-117"></div>
              </div>
              <div data-id="element-118">
                <div className="text-xs mb-1" data-id="element-119">
                  MAP B: {emotion2.name.toUpperCase()}
                </div>
                <div className="w-4 h-4 border border-white" style={{
              backgroundColor: useCustomColors ? sphere2Color : `rgb(${Math.round(emotion2.color.r * 255)}, ${Math.round(emotion2.color.g * 255)}, ${Math.round(emotion2.color.b * 255)})`
            }} data-id="element-120"></div>
              </div>
            </div>
            <div className="text-[0.6rem] mt-2 opacity-80" data-id="element-121">
              {location.hash.includes('embedded=true') ? 'Use mouse: Left-click & drag to rotate, right-click & drag to pan' : 'Use mouse: Left-click & drag to rotate, scroll to zoom, right-click & drag to pan'}
            </div>
          </div>
          {/* Visualization Mode Switcher */}
          <div className={`absolute top-12 right-4 bg-black bg-opacity-70 p-2 border border-white text-xs transition-opacity duration-300 ${modePanelVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} data-id="element-122">
            <div className="flex justify-between items-center mb-2" data-id="element-123">
              <div className="font-bold" data-id="element-124">VISUALIZATION MODE</div>
              <button onClick={toggleModePanel} className="ml-2 w-5 h-5 flex items-center justify-center border border-white" data-id="element-125">
                {modePanelVisible ? '−' : '+'}
              </button>
            </div>
            <div className="flex flex-col space-y-2" data-id="element-126">
              <button onClick={() => handleModeChange('baseline')} className={`px-3 py-2 border ${visualizationMode === 'baseline' ? 'bg-white text-black' : 'border-white text-white'} text-left transition-colors hover:bg-white hover:bg-opacity-20`} data-id="element-127">
                <div className="font-bold" data-id="element-128">BASELINE</div>
                <div className="text-[0.6rem] mt-1 opacity-80" data-id="element-129">
                  Original interaction pattern
                </div>
              </button>
              <button onClick={() => handleModeChange('resonanceTransfer')} className={`px-3 py-2 border ${visualizationMode === 'resonanceTransfer' ? 'bg-white text-black' : 'border-white text-white'} text-left transition-colors hover:bg-white hover:bg-opacity-20`} data-id="element-130">
                <div className="font-bold" data-id="element-131">RESONANCE TRANSFER</div>
                <div className="text-[0.6rem] mt-1 opacity-80" data-id="element-132">
                  Emotion flows from Sphere A to B
                </div>
              </button>
              <button onClick={() => handleModeChange('interferenceImpact')} className={`px-3 py-2 border ${visualizationMode === 'interferenceImpact' ? 'bg-white text-black' : 'border-white text-white'} text-left transition-colors hover:bg-white hover:bg-opacity-20`} data-id="element-133">
                <div className="font-bold" data-id="element-134">INTERFERENCE IMPACT</div>
                <div className="text-[0.6rem] mt-1 opacity-80" data-id="element-135">
                  Emotions collide and create patterns
                </div>
              </button>
              <button onClick={() => handleModeChange('entangledFusion')} className={`px-3 py-2 border ${visualizationMode === 'entangledFusion' ? 'bg-white text-black' : 'border-white text-white'} text-left transition-colors hover:bg-white hover:bg-opacity-20`} data-id="element-136">
                <div className="font-bold" data-id="element-137">ENTANGLED FUSION</div>
                <div className="text-[0.6rem] mt-1 opacity-80" data-id="element-138">
                  Emotions combine into new state
                </div>
              </button>
            </div>
          </div>
          {/* Controls tabs */}
          <div className={`absolute bottom-4 left-4 bg-black bg-opacity-70 p-2 border border-white text-xs w-72 transition-opacity duration-300 ${controlPanelVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} data-id="element-139">
            <div className="flex justify-between items-center border-b border-white mb-2" data-id="element-140">
              <div className="flex flex-wrap" data-id="element-141">
                <button className={`px-2 py-1 ${activeControlPanel === 'particles' ? 'bg-white text-black' : 'text-white'}`} onClick={() => setActiveControlPanel('particles')} data-id="element-142">
                  PARTICLES
                </button>
                <button className={`px-2 py-1 ${activeControlPanel === 'glow' ? 'bg-white text-black' : 'text-white'}`} onClick={() => setActiveControlPanel('glow')} data-id="element-143">
                  GLOW
                </button>
                <button className={`px-2 py-1 ${activeControlPanel === 'trails' ? 'bg-white text-black' : 'text-white'}`} onClick={() => setActiveControlPanel('trails')} data-id="element-144">
                  TRAILS
                </button>
                <button className={`px-2 py-1 ${activeControlPanel === 'spheres' ? 'bg-white text-black' : 'text-white'}`} onClick={() => setActiveControlPanel('spheres')} data-id="element-145">
                  SPHERES
                </button>
                <button className={`px-2 py-1 ${activeControlPanel === 'colors' ? 'bg-white text-black' : 'text-white'}`} onClick={() => setActiveControlPanel('colors')} data-id="element-146">
                  COLORS
                </button>
              </div>
              <button onClick={toggleControlPanel} className="ml-2 w-5 h-5 flex items-center justify-center border border-white" data-id="element-147">
                {controlPanelVisible ? '−' : '+'}
              </button>
            </div>
            {/* Particle Controls Panel */}
            {activeControlPanel === 'particles' && <>
                <div className="font-bold mb-2" data-id="element-148">PARTICLE CONTROLS</div>
                <div className="mb-2" data-id="element-149">
                  <div className="flex justify-between mb-1" data-id="element-150">
                    <label htmlFor="speed" className="text-xs" data-id="element-151">
                      SPEED: {particleSpeed.toFixed(1)}x
                    </label>
                  </div>
                  <input id="speed" type="range" min="0.1" max="3" step="0.1" value={particleSpeed} onChange={handleSpeedChange} className="w-full appearance-none h-1 bg-white rounded-none outline-none [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border [&::-webkit-slider-thumb]:border-black [&::-webkit-slider-thumb]:rounded-none" data-id="element-152" />
                </div>
                <div className="mb-2" data-id="element-153">
                  <div className="flex justify-between mb-1" data-id="element-154">
                    <label htmlFor="size" className="text-xs" data-id="element-155">
                      SIZE: {particleSize.toFixed(1)}x
                    </label>
                  </div>
                  <input id="size" type="range" min="0.1" max="3" step="0.1" value={particleSize} onChange={handleSizeChange} className="w-full appearance-none h-1 bg-white rounded-none outline-none [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border [&::-webkit-slider-thumb]:border-black [&::-webkit-slider-thumb]:rounded-none" data-id="element-156" />
                </div>
                <div className="mb-2" data-id="element-157">
                  <div className="flex justify-between mb-1" data-id="element-158">
                    <label htmlFor="count" className="text-xs" data-id="element-159">
                      COUNT: {particleCount}
                    </label>
                  </div>
                  <input id="count" type="range" min="1000" max="20000" step="1000" value={particleCount} onChange={handleCountChange} className="w-full appearance-none h-1 bg-white rounded-none outline-none [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border [&::-webkit-slider-thumb]:border-black [&::-webkit-slider-thumb]:rounded-none" data-id="element-160" />
                </div>
                <div className="mb-2" data-id="element-161">
                  <div className="flex justify-between mb-1" data-id="element-162">
                    <label htmlFor="movementAmplification" className="text-xs" data-id="element-163">
                      MOVEMENT AMPLIFICATION: {movementAmplification.toFixed(1)}
                      x
                    </label>
                  </div>
                  <input id="movementAmplification" type="range" min="0.1" max="10" step="0.1" value={movementAmplification} onChange={handleMovementAmplificationChange} className="w-full appearance-none h-1 bg-white rounded-none outline-none [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border [&::-webkit-slider-thumb]:border-black [&::-webkit-slider-thumb]:rounded-none" data-id="element-164" />
                </div>
              </>}
            {/* Glow Controls Panel */}
            {activeControlPanel === 'glow' && <>
                <div className="font-bold mb-2" data-id="element-165">GLOW CONTROLS</div>
                <div className="mb-2" data-id="element-166">
                  <div className="flex justify-between mb-1" data-id="element-167">
                    <label htmlFor="glow" className="text-xs" data-id="element-168">
                      INTENSITY: {particleGlow.toFixed(1)}x
                    </label>
                  </div>
                  <input id="glow" type="range" min="0" max="50" step="0.5" value={particleGlow} onChange={handleGlowChange} className="w-full appearance-none h-1 bg-white rounded-none outline-none [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border [&::-webkit-slider-thumb]:border-black [&::-webkit-slider-thumb]:rounded-none" data-id="element-169" />
                </div>
                <div className="mb-2" data-id="element-170">
                  <div className="flex justify-between mb-1" data-id="element-171">
                    <label htmlFor="glowRadius" className="text-xs" data-id="element-172">
                      RADIUS: {glowRadius.toFixed(1)}x
                    </label>
                  </div>
                  <input id="glowRadius" type="range" min="0.5" max="30" step="0.5" value={glowRadius} onChange={handleGlowRadiusChange} className="w-full appearance-none h-1 bg-white rounded-none outline-none [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border [&::-webkit-slider-thumb]:border-black [&::-webkit-slider-thumb]:rounded-none" data-id="element-173" />
                </div>
                <div className="mb-4" data-id="element-174">
                  <div className="flex justify-between mb-1" data-id="element-175">
                    <label htmlFor="glowColor" className="text-xs" data-id="element-176">
                      COLOR:
                    </label>
                    <div className="w-4 h-4 border border-white" style={{
                backgroundColor: glowColor
              }} data-id="element-177"></div>
                  </div>
                  <input id="glowColor" type="color" value={glowColor} onChange={handleGlowColorChange} className="w-full h-6 border border-white bg-transparent cursor-pointer" data-id="element-178" />
                </div>
              </>}
            {/* Trails Controls Panel */}
            {activeControlPanel === 'trails' && <>
                <div className="font-bold mb-2" data-id="element-179">TRAIL CONTROLS</div>
                <div className="mb-4 flex items-center" data-id="element-180">
                  <label htmlFor="trailToggle" className="text-xs mr-2" data-id="element-181">
                    ENABLE TRAILS:
                  </label>
                  <button id="trailToggle" onClick={handleTrailToggle} className={`px-3 py-1 border ${trailEnabled ? 'bg-white text-black' : 'border-white text-white'}`} data-id="element-182">
                    {trailEnabled ? 'ON' : 'OFF'}
                  </button>
                </div>
                <div className={`transition-opacity duration-300 ${trailEnabled ? 'opacity-100' : 'opacity-50 pointer-events-none'}`} data-id="element-183">
                  <div className="mb-2" data-id="element-184">
                    <div className="flex justify-between mb-1" data-id="element-185">
                      <label htmlFor="trailLength" className="text-xs" data-id="element-186">
                        LENGTH: {Math.round(trailLength * 100)}%
                      </label>
                    </div>
                    <input id="trailLength" type="range" min="0.1" max="1" step="0.05" value={trailLength} onChange={handleTrailLengthChange} className="w-full appearance-none h-1 bg-white rounded-none outline-none [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border [&::-webkit-slider-thumb]:border-black [&::-webkit-slider-thumb]:rounded-none" data-id="element-187" />
                  </div>
                  <div className="mb-2" data-id="element-188">
                    <div className="flex justify-between mb-1" data-id="element-189">
                      <label htmlFor="trailBrightness" className="text-xs" data-id="element-190">
                        BRIGHTNESS: {trailBrightness.toFixed(1)}x
                      </label>
                    </div>
                    <input id="trailBrightness" type="range" min="0.1" max="10" step="0.1" value={trailBrightness} onChange={handleTrailBrightnessChange} className="w-full appearance-none h-1 bg-white rounded-none outline-none [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border [&::-webkit-slider-thumb]:border-black [&::-webkit-slider-thumb]:rounded-none" data-id="element-191" />
                  </div>
                  <div className="text-[0.6rem] mt-4 opacity-80" data-id="element-192">
                    Note: Changing trail settings requires reinitializing the
                    visualization
                  </div>
                </div>
              </>}
            {/* Spheres Controls Panel */}
            {activeControlPanel === 'spheres' && <>
                <div className="font-bold mb-2" data-id="element-193">BLOCH SPHERE CONTROLS</div>
                <div className="mb-2" data-id="element-194">
                  <div className="flex justify-between mb-1" data-id="element-195">
                    <label htmlFor="distance" className="text-xs" data-id="element-196">
                      DISTANCE: {sphereDistance.toFixed(1)}
                    </label>
                  </div>
                  <input id="distance" type="range" min="10" max="60" step="1" value={sphereDistance} onChange={handleSphereDistanceChange} className="w-full appearance-none h-1 bg-white rounded-none outline-none [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border [&::-webkit-slider-thumb]:border-black [&::-webkit-slider-thumb]:rounded-none" data-id="element-197" />
                </div>
                <div className="mb-2" data-id="element-198">
                  <div className="flex justify-between mb-1" data-id="element-199">
                    <label htmlFor="sphere1size" className="text-xs" data-id="element-200">
                      SPHERE A SIZE: {sphere1Size.toFixed(1)}x
                    </label>
                  </div>
                  <input id="sphere1size" type="range" min="0.5" max="2.0" step="0.1" value={sphere1Size} onChange={handleSphere1SizeChange} className="w-full appearance-none h-1 bg-white rounded-none outline-none [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border [&::-webkit-slider-thumb]:border-black [&::-webkit-slider-thumb]:rounded-none" data-id="element-201" />
                </div>
                <div className="mb-2" data-id="element-202">
                  <div className="flex justify-between mb-1" data-id="element-203">
                    <label htmlFor="sphere2size" className="text-xs" data-id="element-204">
                      SPHERE B SIZE: {sphere2Size.toFixed(1)}x
                    </label>
                  </div>
                  <input id="sphere2size" type="range" min="0.5" max="2.0" step="0.1" value={sphere2Size} onChange={handleSphere2SizeChange} className="w-full appearance-none h-1 bg-white rounded-none outline-none [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border [&::-webkit-slider-thumb]:border-black [&::-webkit-slider-thumb]:rounded-none" data-id="element-205" />
                </div>
              </>}
            {/* Colors Controls Panel */}
            {activeControlPanel === 'colors' && <>
                <div className="font-bold mb-2" data-id="element-206">COLOR CONTROLS</div>
                <div className="mb-3" data-id="element-207">
                  <div className="flex justify-between mb-1" data-id="element-208">
                    <label htmlFor="backgroundColor" className="text-xs" data-id="element-209">
                      BACKGROUND COLOR:
                    </label>
                    <div className="w-4 h-4 border border-white" style={{
                backgroundColor: backgroundColor
              }} data-id="element-210"></div>
                  </div>
                  <input id="backgroundColor" type="color" value={backgroundColor} onChange={handleBackgroundColorChange} className="w-full h-6 border border-white bg-transparent cursor-pointer" data-id="element-211" />
                </div>
                <div className="mb-4 flex items-center" data-id="element-212">
                  <label htmlFor="useCustomColors" className="text-xs mr-2" data-id="element-213">
                    USE CUSTOM COLORS:
                  </label>
                  <button id="useCustomColors" onClick={handleUseCustomColorsToggle} className={`px-3 py-1 border ${useCustomColors ? 'bg-white text-black' : 'border-white text-white'}`} data-id="element-214">
                    {useCustomColors ? 'ON' : 'OFF'}
                  </button>
                </div>
                <div className={`transition-opacity duration-300 ${useCustomColors ? 'opacity-100' : 'opacity-50 pointer-events-none'}`} data-id="element-215">
                  <div className="mb-3" data-id="element-216">
                    <div className="flex justify-between mb-1" data-id="element-217">
                      <label htmlFor="sphere1Color" className="text-xs" data-id="element-218">
                        SPHERE A COLOR:
                      </label>
                      <div className="w-4 h-4 border border-white" style={{
                  backgroundColor: sphere1Color
                }} data-id="element-219"></div>
                    </div>
                    <input id="sphere1Color" type="color" value={sphere1Color} onChange={handleSphere1ColorChange} className="w-full h-6 border border-white bg-transparent cursor-pointer" data-id="element-220" />
                  </div>
                  <div className="mb-3" data-id="element-221">
                    <div className="flex justify-between mb-1" data-id="element-222">
                      <label htmlFor="sphere2Color" className="text-xs" data-id="element-223">
                        SPHERE B COLOR:
                      </label>
                      <div className="w-4 h-4 border border-white" style={{
                  backgroundColor: sphere2Color
                }} data-id="element-224"></div>
                    </div>
                    <input id="sphere2Color" type="color" value={sphere2Color} onChange={handleSphere2ColorChange} className="w-full h-6 border border-white bg-transparent cursor-pointer" data-id="element-225" />
                  </div>
                  <div className="mb-2" data-id="element-226">
                    <div className="flex justify-between mb-1" data-id="element-227">
                      <label htmlFor="colorGradientSteps" className="text-xs" data-id="element-228">
                        GRADIENT STEPS: {colorGradientSteps}
                      </label>
                    </div>
                    <input id="colorGradientSteps" type="range" min="3" max="12" step="1" value={colorGradientSteps} onChange={handleColorGradientStepsChange} className="w-full appearance-none h-1 bg-white rounded-none outline-none [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border [&::-webkit-slider-thumb]:border-black [&::-webkit-slider-thumb]:rounded-none" data-id="element-229" />
                  </div>
                  <div className="mb-2" data-id="element-230">
                    <div className="flex justify-between mb-1" data-id="element-231">
                      <label htmlFor="colorIntensity" className="text-xs" data-id="element-232">
                        COLOR INTENSITY: {colorIntensity.toFixed(1)}x
                      </label>
                    </div>
                    <input id="colorIntensity" type="range" min="0.5" max="3.0" step="0.1" value={colorIntensity} onChange={handleColorIntensityChange} className="w-full appearance-none h-1 bg-white rounded-none outline-none [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border [&::-webkit-slider-thumb]:border-black [&::-webkit-slider-thumb]:rounded-none" data-id="element-233" />
                  </div>
                  <div className="text-[0.6rem] mt-4 opacity-80" data-id="element-234">
                    Colors will create a gradient between spheres based on
                    particle position
                  </div>
                </div>
              </>}
          </div>
        </>}
      {/* Custom alert overlay */}
      {showAlert && <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 bg-black bg-opacity-90 border border-white text-white px-6 py-4 text-center animate-fadeIn" data-id="element-235">
          {alertMessage}
        </div>}
      {/* Embed code modal */}
      {showEmbedModal && <div className="absolute top-0 left-0 w-full h-full bg-black bg-opacity-80 flex items-center justify-center z-50" data-id="element-236">
          <div className="bg-black border-2 border-white text-white p-6 max-w-2xl w-full mx-4" data-id="element-237">
            <div className="flex justify-between items-center mb-4" data-id="element-238">
              <h3 className="text-lg font-bold" data-id="element-239">EMBED VISUALIZATION</h3>
              <button onClick={closeEmbedModal} className="w-6 h-6 flex items-center justify-center border border-white" data-id="element-240">
                ×
              </button>
            </div>
            <p className="mb-4 text-sm" data-id="element-241">
              Copy the code below to embed this exact visualization on your
              website:
            </p>
            <div className="bg-gray-900 border border-white p-3 mb-4 overflow-auto max-h-40" data-id="element-242">
              <pre className="text-xs whitespace-pre-wrap" data-id="element-243">{embedCode}</pre>
            </div>
            <div className="flex justify-end" data-id="element-244">
              <button onClick={copyEmbedCode} className="bg-white text-black px-4 py-2 text-sm" data-id="element-245">
                COPY CODE
              </button>
            </div>
          </div>
        </div>}
      {/* CSS animation for alert fade in/out */}
      <style jsx data-id="element-246">{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translate(-50%, -50%) scale(0.9);
          }
          to {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
        }
      `}</style>
    </div>;
};